import React, { useEffect, useMemo, useState } from "react";
import { Check, X, ChevronRight, RotateCcw, Award, Beaker, Heart, Activity, Droplet } from "lucide-react";
import { applyBoost, GAME_BOOST } from "../lib/tamagotchiUtils";

const LabInterpreter = ({ onBack, userData, saveUserData }) => {
  const storageKey = "labInterpreterProgress";
  const [gamePhase, setGamePhase] = useState("category"); // category, playing, complete
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentCase, setCurrentCase] = useState(null);
  const [caseIndex, setCaseIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [casesCompleted, setCasesCompleted] = useState(0);
  const [activeCases, setActiveCases] = useState([]);
  const [progressByCategory, setProgressByCategory] = useState({});

  const loadSavedProgress = () => {
    if (typeof window === "undefined") return null;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  };

  const saveProgress = (nextProgress) => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(nextProgress));
    } catch (error) {
      // Ignore storage failures
    }
  };

  // Categories
  const categories = [
    {
      id: "hematology",
      name: "Hematology",
      description: "Blood cell counts and coagulation",
      icon: Droplet,
      color: "from-red-100 to-rose-100",
      borderColor: "border-red-300",
      iconColor: "text-red-600",
      coins: 15,
      xp: 30,
    },
    {
      id: "chemistry",
      name: "Chemistry Panel",
      description: "Electrolytes, kidney, and liver function",
      icon: Beaker,
      color: "from-blue-100 to-cyan-100",
      borderColor: "border-blue-300",
      iconColor: "text-blue-600",
      coins: 20,
      xp: 40,
    },
    {
      id: "cardiac",
      name: "Cardiac Markers",
      description: "Troponin, BNP, and lipid panels",
      icon: Heart,
      color: "from-pink-100 to-red-100",
      borderColor: "border-pink-300",
      iconColor: "text-pink-600",
      coins: 20,
      xp: 40,
    },
    {
      id: "endocrine",
      name: "Endocrine Labs",
      description: "Thyroid, glucose, and hormone levels",
      icon: Activity,
      color: "from-purple-100 to-violet-100",
      borderColor: "border-purple-300",
      iconColor: "text-purple-600",
      coins: 25,
      xp: 50,
    },
  ];

  // Lab cases database
  const labCases = useMemo(() => ({
    hematology: [
      {
        id: "heme-1",
        patientInfo: "32-year-old female with fatigue and pale skin",
        labResults: [
          { test: "Hemoglobin", value: "8.2 g/dL", normal: "12-16 g/dL", abnormal: true },
          { test: "Hematocrit", value: "25%", normal: "36-46%", abnormal: true },
          { test: "MCV", value: "68 fL", normal: "80-100 fL", abnormal: true },
          { test: "WBC", value: "7,500/μL", normal: "4,000-11,000/μL", abnormal: false },
          { test: "Platelets", value: "280,000/μL", normal: "150,000-400,000/μL", abnormal: false },
        ],
        additionalInfo: "Patient reports heavy menstrual periods",
        correctAnswer: "Iron Deficiency Anemia",
        options: [
          "Iron Deficiency Anemia",
          "Vitamin B12 Deficiency",
          "Thalassemia",
          "Acute Blood Loss",
        ],
        explanation: "Low hemoglobin and hematocrit with microcytic (low MCV) anemia suggests iron deficiency. Heavy menstrual bleeding is a common cause in premenopausal women. Iron studies would confirm with low ferritin and low serum iron.",
      },
      {
        id: "heme-2",
        patientInfo: "68-year-old male with weakness and numbness in feet",
        labResults: [
          { test: "Hemoglobin", value: "9.5 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "MCV", value: "112 fL", normal: "80-100 fL", abnormal: true },
          { test: "WBC", value: "6,200/μL", normal: "4,000-11,000/μL", abnormal: false },
          { test: "Platelets", value: "140,000/μL", normal: "150,000-400,000/μL", abnormal: true },
        ],
        additionalInfo: "Vegan diet for past 10 years",
        correctAnswer: "Vitamin B12 Deficiency",
        options: [
          "Vitamin B12 Deficiency",
          "Folate Deficiency",
          "Iron Deficiency Anemia",
          "Myelodysplastic Syndrome",
        ],
        explanation: "Macrocytic anemia (high MCV >100) with neurological symptoms (numbness) in a vegan patient strongly suggests B12 deficiency. B12 is primarily found in animal products. Folate deficiency causes similar lab findings but no neurological symptoms.",
      },
      {
        id: "heme-3",
        patientInfo: "55-year-old female with easy bruising and petechiae",
        labResults: [
          { test: "Platelets", value: "28,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "Hemoglobin", value: "13.5 g/dL", normal: "12-16 g/dL", abnormal: false },
          { test: "WBC", value: "8,000/μL", normal: "4,000-11,000/μL", abnormal: false },
          { test: "PT/INR", value: "Normal", normal: "Normal", abnormal: false },
          { test: "PTT", value: "Normal", normal: "Normal", abnormal: false },
        ],
        additionalInfo: "No recent illness or medication changes",
        correctAnswer: "Immune Thrombocytopenic Purpura (ITP)",
        options: [
          "Immune Thrombocytopenic Purpura (ITP)",
          "Disseminated Intravascular Coagulation (DIC)",
          "Thrombotic Thrombocytopenic Purpura (TTP)",
          "Aplastic Anemia",
        ],
        explanation: "Isolated severe thrombocytopenia with normal hemoglobin, WBC, and coagulation studies suggests ITP (immune destruction of platelets). DIC would show abnormal PT/PTT. TTP would have schistocytes and hemolytic anemia. Aplastic anemia affects all cell lines.",
      },
      {
        id: "heme-4",
        patientInfo: "45-year-old male with fatigue and recurrent infections",
        labResults: [
          { test: "WBC", value: "52,000/μL", normal: "4,000-11,000/μL", abnormal: true },
          { test: "Hemoglobin", value: "10.2 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "Platelets", value: "85,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "Peripheral smear", value: "Blast cells >20%", normal: "No blasts", abnormal: true },
        ],
        additionalInfo: "Night sweats, bone pain, splenomegaly on exam",
        correctAnswer: "Acute Leukemia",
        options: [
          "Acute Leukemia",
          "Chronic Lymphocytic Leukemia",
          "Mononucleosis",
          "Leukemoid Reaction",
        ],
        explanation: "Very high WBC with blast cells (>20%), anemia, and thrombocytopenia indicates acute leukemia. The presence of blasts in peripheral blood distinguishes it from chronic leukemias. Bone marrow biopsy is needed to determine if AML or ALL.",
      },
      {
        id: "heme-5",
        patientInfo: "28-year-old African American male with severe bone pain",
        labResults: [
          { test: "Hemoglobin", value: "7.5 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "Reticulocyte count", value: "8%", normal: "0.5-2%", abnormal: true },
          { test: "LDH", value: "450 U/L", normal: "140-280 U/L", abnormal: true },
          { test: "Indirect bilirubin", value: "3.2 mg/dL", normal: "0.1-1.0 mg/dL", abnormal: true },
        ],
        additionalInfo: "Hemoglobin electrophoresis shows HbS 85%, history of similar episodes",
        correctAnswer: "Sickle Cell Crisis",
        options: [
          "Sickle Cell Crisis",
          "Acute Chest Syndrome",
          "Autoimmune Hemolytic Anemia",
          "G6PD Deficiency",
        ],
        explanation: "Severe anemia with evidence of hemolysis (elevated reticulocytes, LDH, indirect bilirubin) and HbS on electrophoresis confirms sickle cell disease. Bone pain crisis is triggered by sickling of RBCs causing vaso-occlusion.",
      },
      {
        id: "heme-6",
        patientInfo: "60-year-old female on warfarin for atrial fibrillation",
        labResults: [
          { test: "INR", value: "6.8", normal: "2.0-3.0 (therapeutic)", abnormal: true },
          { test: "PT", value: "68 seconds", normal: "11-13.5 seconds", abnormal: true },
          { test: "PTT", value: "45 seconds", normal: "25-35 seconds", abnormal: true },
          { test: "Hemoglobin", value: "9.8 g/dL", normal: "12-16 g/dL", abnormal: true },
        ],
        additionalInfo: "Recently started antibiotics for UTI, black tarry stools",
        correctAnswer: "Warfarin Toxicity with GI Bleeding",
        options: [
          "Warfarin Toxicity with GI Bleeding",
          "Vitamin K Deficiency",
          "Liver Failure",
          "Disseminated Intravascular Coagulation",
        ],
        explanation: "Supratherapeutic INR (>3.0) on warfarin with signs of bleeding (anemia, melena). Antibiotics can potentiate warfarin by killing gut flora that produce vitamin K. Treatment includes holding warfarin, giving vitamin K, and possibly FFP or PCC.",
      },
      {
        id: "heme-7",
        patientInfo: "35-year-old male with spontaneous nosebleeds and prolonged bleeding after cuts",
        labResults: [
          { test: "Platelets", value: "220,000/μL", normal: "150,000-400,000/μL", abnormal: false },
          { test: "PT/INR", value: "Normal", normal: "Normal", abnormal: false },
          { test: "PTT", value: "72 seconds", normal: "25-35 seconds", abnormal: true },
          { test: "Bleeding time", value: "Prolonged", normal: "2-7 minutes", abnormal: true },
        ],
        additionalInfo: "Family history of bleeding disorder in maternal uncle",
        correctAnswer: "Hemophilia A or B",
        options: [
          "Hemophilia A or B",
          "Von Willebrand Disease",
          "Platelet Dysfunction",
          "Factor XII Deficiency",
        ],
        explanation: "Isolated prolonged PTT with normal PT and platelets suggests intrinsic pathway defect. X-linked inheritance pattern (maternal uncle) points to hemophilia. Factor VIII (Hemophilia A) or Factor IX (Hemophilia B) assay will confirm.",
      },
      {
        id: "heme-8",
        patientInfo: "72-year-old male with fatigue and recurrent pneumonia",
        labResults: [
          { test: "WBC", value: "85,000/μL", normal: "4,000-11,000/μL", abnormal: true },
          { test: "Lymphocytes", value: "92%", normal: "20-40%", abnormal: true },
          { test: "Hemoglobin", value: "10.8 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "Platelets", value: "95,000/μL", normal: "150,000-400,000/μL", abnormal: true },
        ],
        additionalInfo: "Enlarged lymph nodes, smudge cells on peripheral smear",
        correctAnswer: "Chronic Lymphocytic Leukemia (CLL)",
        options: [
          "Chronic Lymphocytic Leukemia (CLL)",
          "Acute Lymphoblastic Leukemia",
          "Infectious Mononucleosis",
          "Lymphoma with Leukemic Phase",
        ],
        explanation: "Elderly patient with marked lymphocytosis, smudge cells (fragile lymphocytes), lymphadenopathy, and cytopenias is classic for CLL. Unlike acute leukemia, cells are mature-appearing lymphocytes, not blasts.",
      },
      {
        id: "heme-9",
        patientInfo: "40-year-old female with fatigue, fever, and mucosal bleeding",
        labResults: [
          { test: "Platelets", value: "18,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "PT", value: "18 seconds", normal: "11-13.5 seconds", abnormal: true },
          { test: "PTT", value: "52 seconds", normal: "25-35 seconds", abnormal: true },
          { test: "Fibrinogen", value: "85 mg/dL", normal: "200-400 mg/dL", abnormal: true },
          { test: "D-dimer", value: "Markedly elevated", normal: "<500 ng/mL", abnormal: true },
        ],
        additionalInfo: "Septic from urinary tract infection, petechiae and oozing from IV sites",
        correctAnswer: "Disseminated Intravascular Coagulation (DIC)",
        options: [
          "Disseminated Intravascular Coagulation (DIC)",
          "Immune Thrombocytopenic Purpura",
          "Liver Failure",
          "Vitamin K Deficiency",
        ],
        explanation: "Sepsis-induced DIC shows consumption of clotting factors (prolonged PT/PTT), low fibrinogen, low platelets, and very high D-dimer (fibrin degradation). DIC causes both clotting and bleeding simultaneously.",
      },
      {
        id: "heme-10",
        patientInfo: "25-year-old female with fatigue during menstruation",
        labResults: [
          { test: "Hemoglobin", value: "10.5 g/dL", normal: "12-16 g/dL", abnormal: true },
          { test: "MCV", value: "72 fL", normal: "80-100 fL", abnormal: true },
          { test: "Ferritin", value: "8 ng/mL", normal: "12-150 ng/mL", abnormal: true },
          { test: "TIBC", value: "450 μg/dL", normal: "250-370 μg/dL", abnormal: true },
          { test: "Serum Iron", value: "25 μg/dL", normal: "60-170 μg/dL", abnormal: true },
        ],
        additionalInfo: "Heavy periods, vegetarian diet, mild koilonychia (spoon nails)",
        correctAnswer: "Iron Deficiency Anemia",
        options: [
          "Iron Deficiency Anemia",
          "Thalassemia Trait",
          "Anemia of Chronic Disease",
          "Sideroblastic Anemia",
        ],
        explanation: "Classic iron studies: low ferritin (iron stores), low serum iron, high TIBC (body trying to absorb more iron). Microcytic anemia with low ferritin confirms iron deficiency. Heavy menses and vegetarian diet are risk factors.",
      },
      {
        id: "heme-11",
        patientInfo: "50-year-old male with headaches and blurred vision",
        labResults: [
          { test: "Hemoglobin", value: "19.5 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "Hematocrit", value: "58%", normal: "40-54%", abnormal: true },
          { test: "WBC", value: "14,000/μL", normal: "4,000-11,000/μL", abnormal: true },
          { test: "Platelets", value: "650,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "EPO level", value: "Low", normal: "Normal", abnormal: true },
        ],
        additionalInfo: "Facial plethora (ruddy complexion), splenomegaly, pruritus after bathing",
        correctAnswer: "Polycythemia Vera",
        options: [
          "Polycythemia Vera",
          "Secondary Polycythemia",
          "Dehydration",
          "Chronic Hypoxia (COPD)",
        ],
        explanation: "Elevated RBCs, WBCs, and platelets with LOW EPO indicates primary bone marrow disorder (polycythemia vera). Secondary causes have HIGH EPO. JAK2 mutation is present in 95% of cases. Increased blood viscosity causes symptoms.",
      },
      {
        id: "heme-12",
        patientInfo: "28-year-old female with recurrent miscarriages and leg swelling",
        labResults: [
          { test: "Hemoglobin", value: "13.8 g/dL", normal: "12-16 g/dL", abnormal: false },
          { test: "Platelets", value: "95,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "PT", value: "12 seconds", normal: "11-13 seconds", abnormal: false },
          { test: "PTT", value: "52 seconds", normal: "25-35 seconds", abnormal: true },
          { test: "Lupus anticoagulant", value: "Positive", normal: "Negative", abnormal: true },
        ],
        additionalInfo: "History of DVT at age 24, positive anti-cardiolipin antibodies",
        correctAnswer: "Antiphospholipid Syndrome",
        options: [
          "Antiphospholipid Syndrome",
          "Lupus with Hematologic Involvement",
          "Factor V Leiden",
          "Heparin-Induced Thrombocytopenia",
        ],
        explanation: "Prolonged PTT with positive lupus anticoagulant and anti-cardiolipin antibodies, combined with arterial/venous thrombosis and recurrent pregnancy loss, defines antiphospholipid syndrome. Paradoxically, the prolonged PTT indicates a pro-thrombotic state in vivo.",
      },
      {
        id: "heme-13",
        patientInfo: "6-year-old male with severe bone pain and fever",
        labResults: [
          { test: "Hemoglobin", value: "6.5 g/dL", normal: "11.5-15.5 g/dL", abnormal: true },
          { test: "WBC", value: "18,000/μL", normal: "5,000-14,500/μL", abnormal: true },
          { test: "Reticulocytes", value: "15%", normal: "0.5-2.5%", abnormal: true },
          { test: "LDH", value: "850 U/L", normal: "140-280 U/L", abnormal: true },
          { test: "Bilirubin (indirect)", value: "4.2 mg/dL", normal: "0.2-0.8 mg/dL", abnormal: true },
        ],
        additionalInfo: "African American, dactylitis as an infant, hemoglobin electrophoresis shows HbS 85%",
        correctAnswer: "Sickle Cell Vaso-Occlusive Crisis",
        options: [
          "Sickle Cell Vaso-Occlusive Crisis",
          "Acute Osteomyelitis",
          "Aplastic Crisis",
          "Autoimmune Hemolytic Anemia",
        ],
        explanation: "High reticulocyte count with elevated LDH and indirect bilirubin indicates ongoing hemolysis. In a known sickle cell patient, severe bone pain with fever suggests vaso-occlusive crisis. Aplastic crisis would show LOW reticulocytes.",
      },
      {
        id: "heme-14",
        patientInfo: "55-year-old male with back pain and recurrent infections",
        labResults: [
          { test: "Hemoglobin", value: "9.0 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "Total Protein", value: "11.2 g/dL", normal: "6.0-8.3 g/dL", abnormal: true },
          { test: "Albumin", value: "3.2 g/dL", normal: "3.5-5.0 g/dL", abnormal: true },
          { test: "Calcium", value: "12.5 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Creatinine", value: "2.8 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
        ],
        additionalInfo: "Protein gap (Total protein - Albumin = 8.0), Rouleaux formation on blood smear",
        correctAnswer: "Multiple Myeloma",
        options: [
          "Multiple Myeloma",
          "Waldenström Macroglobulinemia",
          "Chronic Lymphocytic Leukemia",
          "Metastatic Prostate Cancer",
        ],
        explanation: "Classic tetrad: anemia, hypercalcemia, renal insufficiency, and elevated protein gap (monoclonal protein). Rouleaux formation occurs due to high immunoglobulin levels. Bone pain from lytic lesions. SPEP/UPEP would confirm M-spike.",
      },
      {
        id: "heme-15",
        patientInfo: "35-year-old female with fatigue and dark urine in the morning",
        labResults: [
          { test: "Hemoglobin", value: "8.5 g/dL", normal: "12-16 g/dL", abnormal: true },
          { test: "Reticulocytes", value: "8%", normal: "0.5-2.5%", abnormal: true },
          { test: "LDH", value: "620 U/L", normal: "140-280 U/L", abnormal: true },
          { test: "Haptoglobin", value: "<10 mg/dL", normal: "30-200 mg/dL", abnormal: true },
          { test: "Direct Coombs", value: "Negative", normal: "Negative", abnormal: false },
        ],
        additionalInfo: "Flow cytometry shows CD55 and CD59 deficiency on RBCs",
        correctAnswer: "Paroxysmal Nocturnal Hemoglobinuria (PNH)",
        options: [
          "Paroxysmal Nocturnal Hemoglobinuria (PNH)",
          "Autoimmune Hemolytic Anemia",
          "G6PD Deficiency",
          "Hereditary Spherocytosis",
        ],
        explanation: "Coombs-negative hemolytic anemia with absent haptoglobin indicates intravascular hemolysis. CD55/CD59 deficiency on flow cytometry is diagnostic of PNH. Morning hemoglobinuria occurs due to complement activation during sleep (respiratory acidosis).",
      },
      {
        id: "heme-16",
        patientInfo: "70-year-old female with fatigue and recurrent infections",
        labResults: [
          { test: "Hemoglobin", value: "9.2 g/dL", normal: "12-16 g/dL", abnormal: true },
          { test: "MCV", value: "105 fL", normal: "80-100 fL", abnormal: true },
          { test: "WBC", value: "3,200/μL", normal: "4,000-11,000/μL", abnormal: true },
          { test: "Platelets", value: "85,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "Reticulocytes", value: "0.8%", normal: "0.5-2.5%", abnormal: false },
        ],
        additionalInfo: "Bone marrow shows dysplastic changes and 8% blasts, ring sideroblasts present",
        correctAnswer: "Myelodysplastic Syndrome (MDS)",
        options: [
          "Myelodysplastic Syndrome (MDS)",
          "Aplastic Anemia",
          "Acute Myeloid Leukemia",
          "Megaloblastic Anemia",
        ],
        explanation: "Pancytopenia with macrocytosis and low reticulocytes suggests bone marrow failure. Dysplastic changes and <20% blasts confirm MDS. Ring sideroblasts indicate a specific MDS subtype. AML requires ≥20% blasts.",
      },
      {
        id: "heme-17",
        patientInfo: "22-year-old male with easy bruising and gum bleeding",
        labResults: [
          { test: "Hemoglobin", value: "14.5 g/dL", normal: "14-18 g/dL", abnormal: false },
          { test: "Platelets", value: "245,000/μL", normal: "150,000-400,000/μL", abnormal: false },
          { test: "PT", value: "12 seconds", normal: "11-13 seconds", abnormal: false },
          { test: "PTT", value: "58 seconds", normal: "25-35 seconds", abnormal: true },
          { test: "Bleeding time", value: "12 minutes", normal: "2-7 minutes", abnormal: true },
        ],
        additionalInfo: "Family history of bleeding in males, PTT corrects with mixing study",
        correctAnswer: "Von Willebrand Disease",
        options: [
          "Von Willebrand Disease",
          "Hemophilia A",
          "Hemophilia B",
          "Platelet Function Disorder",
        ],
        explanation: "Prolonged PTT that corrects with mixing study plus prolonged bleeding time suggests von Willebrand disease. vWF carries Factor VIII (causing PTT prolongation) and is essential for platelet adhesion (causing bleeding time prolongation). Hemophilia has normal bleeding time.",
      },
      {
        id: "heme-18",
        patientInfo: "45-year-old female post-hip replacement surgery with dropping platelet count",
        labResults: [
          { test: "Hemoglobin", value: "10.8 g/dL", normal: "12-16 g/dL", abnormal: true },
          { test: "Platelets Day 1", value: "285,000/μL", normal: "150,000-400,000/μL", abnormal: false },
          { test: "Platelets Day 7", value: "45,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "PT", value: "12.5 seconds", normal: "11-13 seconds", abnormal: false },
          { test: "PTT", value: "32 seconds", normal: "25-35 seconds", abnormal: false },
        ],
        additionalInfo: "Receiving unfractionated heparin for DVT prophylaxis, new DVT in contralateral leg",
        correctAnswer: "Heparin-Induced Thrombocytopenia (HIT)",
        options: [
          "Heparin-Induced Thrombocytopenia (HIT)",
          "Dilutional Thrombocytopenia",
          "Immune Thrombocytopenic Purpura",
          "Sepsis-Related Thrombocytopenia",
        ],
        explanation: "The 4T score is high: Thrombocytopenia (>50% drop), Timing (5-10 days after heparin), Thrombosis (new DVT), no other cause. HIT is a prothrombotic condition despite low platelets. Stop all heparin and start alternative anticoagulation immediately.",
      },
      {
        id: "heme-19",
        patientInfo: "3-year-old with petechiae and recent viral infection",
        labResults: [
          { test: "Hemoglobin", value: "12.5 g/dL", normal: "11-14 g/dL", abnormal: false },
          { test: "WBC", value: "8,500/μL", normal: "6,000-17,000/μL", abnormal: false },
          { test: "Platelets", value: "12,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "PT", value: "12 seconds", normal: "11-13 seconds", abnormal: false },
          { test: "PTT", value: "28 seconds", normal: "25-35 seconds", abnormal: false },
        ],
        additionalInfo: "Otherwise healthy, no hepatosplenomegaly, peripheral smear shows large platelets",
        correctAnswer: "Immune Thrombocytopenic Purpura (ITP)",
        options: [
          "Immune Thrombocytopenic Purpura (ITP)",
          "Thrombotic Thrombocytopenic Purpura",
          "Leukemia",
          "Aplastic Anemia",
        ],
        explanation: "Isolated thrombocytopenia with large platelets following viral infection in an otherwise well child is classic ITP. Normal hemoglobin and WBC argue against leukemia or aplastic anemia. TTP would show hemolytic anemia and schistocytes.",
      },
      {
        id: "heme-20",
        patientInfo: "60-year-old male with splenomegaly and night sweats",
        labResults: [
          { test: "Hemoglobin", value: "10.2 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "WBC", value: "125,000/μL", normal: "4,000-11,000/μL", abnormal: true },
          { test: "Platelets", value: "450,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "Basophils", value: "5%", normal: "0-1%", abnormal: true },
          { test: "LAP score", value: "Low", normal: "Normal", abnormal: true },
        ],
        additionalInfo: "Peripheral smear shows full spectrum of myeloid maturation, BCR-ABL positive",
        correctAnswer: "Chronic Myelogenous Leukemia (CML)",
        options: [
          "Chronic Myelogenous Leukemia (CML)",
          "Chronic Lymphocytic Leukemia",
          "Leukemoid Reaction",
          "Primary Myelofibrosis",
        ],
        explanation: "Marked leukocytosis with basophilia, low LAP score, and BCR-ABL positivity (Philadelphia chromosome) confirms CML. Leukemoid reaction has HIGH LAP score. The full spectrum of myeloid cells differentiates from acute leukemia.",
      },
      {
        id: "heme-21",
        patientInfo: "25-year-old pregnant female at 28 weeks with severe anemia",
        labResults: [
          { test: "Hemoglobin", value: "7.8 g/dL", normal: "11-14 g/dL", abnormal: true },
          { test: "Platelets", value: "65,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "LDH", value: "780 U/L", normal: "140-280 U/L", abnormal: true },
          { test: "Peripheral smear", value: "Schistocytes present", normal: "None", abnormal: true },
          { test: "Creatinine", value: "1.8 mg/dL", normal: "0.5-1.1 mg/dL", abnormal: true },
        ],
        additionalInfo: "Blood pressure 165/105 mmHg, proteinuria 3+, elevated liver enzymes",
        correctAnswer: "HELLP Syndrome",
        options: [
          "HELLP Syndrome",
          "Thrombotic Thrombocytopenic Purpura",
          "Hemolytic Uremic Syndrome",
          "Preeclampsia without HELLP",
        ],
        explanation: "HELLP = Hemolysis (elevated LDH, schistocytes), Elevated Liver enzymes, Low Platelets. Occurs in pregnancy with preeclampsia (hypertension, proteinuria). TTP has more severe neurologic symptoms and ADAMTS13 deficiency. Requires urgent delivery.",
      },
      {
        id: "heme-22",
        patientInfo: "28-year-old male of African descent with acute jaundice and dark urine after starting primaquine",
        labResults: [
          { test: "Haemoglobin", value: "7.2 g/dL", normal: "13.5-17.5 g/dL", abnormal: true },
          { test: "MCV", value: "88 fL", normal: "80-100 fL", abnormal: false },
          { test: "Indirect Bilirubin", value: "4.1 mg/dL", normal: "<0.8 mg/dL", abnormal: true },
          { test: "LDH", value: "580 U/L", normal: "140-280 U/L", abnormal: true },
          { test: "Haptoglobin", value: "<0.1 g/L", normal: "0.5-2.5 g/L", abnormal: true },
          { test: "Reticulocytes", value: "8.5%", normal: "0.5-1.5%", abnormal: true },
          { test: "G6PD Activity", value: "15% of normal", normal: "100%", abnormal: true },
        ],
        additionalInfo: "Started antimalarial 5 days ago. Peripheral smear: Heinz bodies (denatured haemoglobin) and bite cells. Direct Coombs test: negative.",
        correctAnswer: "G6PD Deficiency",
        options: [
          "G6PD Deficiency",
          "Autoimmune Haemolytic Anaemia",
          "Hereditary Spherocytosis",
          "Sickle Cell Crisis",
        ],
        explanation: "G6PD deficiency (X-linked) causes episodic intravascular haemolysis after oxidative stress: drugs (primaquine, dapsone, nitrofurantoin), fava beans, infections. Heinz bodies (denatured Hb) and bite cells on smear. Low haptoglobin + elevated LDH + indirect bilirubin = haemolysis. Negative Coombs distinguishes from autoimmune haemolytic anaemia. G6PD activity may be falsely normal acutely as deficient cells are preferentially destroyed.",
      },
      {
        id: "heme-23",
        patientInfo: "19-year-old male with episodic jaundice since childhood, splenomegaly, positive family history",
        labResults: [
          { test: "Haemoglobin", value: "10.2 g/dL", normal: "13.5-17.5 g/dL", abnormal: true },
          { test: "MCV", value: "78 fL", normal: "80-100 fL", abnormal: true },
          { test: "MCHC", value: "37.8 g/dL", normal: "31.5-35.7 g/dL", abnormal: true },
          { test: "Indirect Bilirubin", value: "3.1 mg/dL", normal: "<0.8 mg/dL", abnormal: true },
          { test: "Haptoglobin", value: "0.2 g/L", normal: "0.5-2.5 g/L", abnormal: true },
          { test: "Reticulocytes", value: "6.2%", normal: "0.5-1.5%", abnormal: true },
          { test: "Osmotic Fragility", value: "Increased", normal: "Normal", abnormal: true },
        ],
        additionalInfo: "Peripheral smear: spherocytes without central pallor. Direct Coombs test negative. Father had splenectomy for 'blood problem'.",
        correctAnswer: "Hereditary Spherocytosis",
        options: [
          "Hereditary Spherocytosis",
          "Autoimmune Haemolytic Anaemia",
          "G6PD Deficiency",
          "Iron Deficiency Anaemia",
        ],
        explanation: "Hereditary spherocytosis (usually AD, spectrin/ankyrin mutations): spherocytes lose membrane → high MCHC (most specific finding, >36 g/dL). Extravascular haemolysis (spleen) causes indirect hyperbilirubinaemia and reticulocytosis. Negative Coombs excludes autoimmune cause. Increased osmotic fragility confirms. Complications: gallstones (pigment), aplastic crisis (parvovirus B19). Splenectomy is curative but deferred in children.",
      },
      {
        id: "heme-24",
        patientInfo: "35-year-old male with fatigue, recurrent infections, and spontaneous bruising for 3 months",
        labResults: [
          { test: "Haemoglobin", value: "6.4 g/dL", normal: "13.5-17.5 g/dL", abnormal: true },
          { test: "WBC", value: "1.8 ×10⁹/L", normal: "4.5-11.0 ×10⁹/L", abnormal: true },
          { test: "Neutrophils", value: "0.4 ×10⁹/L", normal: ">1.5 ×10⁹/L", abnormal: true },
          { test: "Platelets", value: "18,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "MCV", value: "92 fL", normal: "80-100 fL", abnormal: false },
          { test: "Reticulocytes", value: "0.1%", normal: "0.5-1.5%", abnormal: true },
          { test: "B12/Folate", value: "Normal", normal: "Normal", abnormal: false },
        ],
        additionalInfo: "Recent history of seronegative hepatitis. Bone marrow biopsy: hypocellular marrow with >75% fat cells, markedly reduced haemopoietic precursors.",
        correctAnswer: "Aplastic Anaemia",
        options: [
          "Aplastic Anaemia",
          "Myelodysplastic Syndrome",
          "Acute Leukaemia",
          "B12/Folate Deficiency Pancytopenia",
        ],
        explanation: "Aplastic anaemia: failure of haematopoietic stem cells → pancytopenia with markedly suppressed reticulocytes (key feature — in destruction, reticulocytes are elevated). Hypocellular bone marrow confirms. Causes: idiopathic autoimmune (T-cell mediated, most common), viral hepatitis, drugs (carbimazole, chloramphenicol), radiation. MCV is normal (not elevated as in B12 deficiency). Treatment: allogeneic SCT (young patients) or immunosuppression (ATG + ciclosporin).",
      },
      {
        id: "heme-25",
        patientInfo: "65-year-old male with headaches, facial plethora, and intense itching after hot showers",
        labResults: [
          { test: "Haemoglobin", value: "20.5 g/dL", normal: "13.5-17.5 g/dL", abnormal: true },
          { test: "Haematocrit", value: "64%", normal: "42-52%", abnormal: true },
          { test: "WBC", value: "14.2 ×10⁹/L", normal: "4.5-11.0 ×10⁹/L", abnormal: true },
          { test: "Platelets", value: "650,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "EPO", value: "2 mIU/mL", normal: "4-26 mIU/mL", abnormal: true },
          { test: "JAK2 V617F", value: "Positive", normal: "Negative", abnormal: true },
        ],
        additionalInfo: "Splenomegaly on examination. Oxygen saturation 98% on air (excludes secondary polycythaemia). Serum ferritin low (iron consumed by erythropoiesis).",
        correctAnswer: "Polycythaemia Vera",
        options: [
          "Polycythaemia Vera",
          "Secondary Polycythaemia",
          "Dehydration",
          "Essential Thrombocythaemia",
        ],
        explanation: "Polycythaemia vera (PV): myeloproliferative neoplasm with autonomous RBC production. JAK2 V617F mutation in >95%. KEY distinguishing feature: SUPPRESSED EPO (in secondary polycythaemia from hypoxia/EPO-secreting tumour, EPO is elevated). Aquagenic pruritus (histamine release from mast cells/basophils) is characteristic. Panmyelosis (elevated WBC and platelets too). Complications: arterial/venous thrombosis, myelofibrosis, AML transformation. Treatment: venesection, aspirin; hydroxycarbamide or ruxolitinib for high-risk.",
      },
      {
        id: "heme-26",
        patientInfo: "55-year-old female with incidentally elevated platelet count, intermittent headaches, and digital ischaemia",
        labResults: [
          { test: "Platelets", value: "1,250,000/μL", normal: "150,000-400,000/μL", abnormal: true },
          { test: "Haemoglobin", value: "13.2 g/dL", normal: "12.0-16.0 g/dL", abnormal: false },
          { test: "WBC", value: "9.5 ×10⁹/L", normal: "4.5-11.0 ×10⁹/L", abnormal: false },
          { test: "JAK2 V617F", value: "Positive", normal: "Negative", abnormal: true },
          { test: "BCR-ABL", value: "Negative", normal: "Negative", abnormal: false },
          { test: "Serum Ferritin", value: "Normal", normal: "Normal", abnormal: false },
        ],
        additionalInfo: "Peripheral smear: giant platelets and platelet clumps. No infection, surgery, or inflammatory cause identified. CALR mutation: negative.",
        correctAnswer: "Essential Thrombocythaemia",
        options: [
          "Essential Thrombocythaemia",
          "Reactive Thrombocytosis",
          "Polycythaemia Vera",
          "CML with Thrombocytosis",
        ],
        explanation: "Essential thrombocythaemia (ET): clonal myeloproliferative neoplasm. JAK2 V617F in 50-60%, CALR in ~25%. Markedly elevated platelets with thrombotic and paradoxically haemorrhagic risk. Normal Hb/RBC distinguishes from PV; normal WBC and negative BCR-ABL excludes CML. Reactive thrombocytosis (infection, iron deficiency, post-splenectomy, inflammation) is self-limiting and has normal ferritin/CRP will be elevated. Treatment: aspirin; hydroxycarbamide or anagrelide for high-risk (age >60, prior thrombosis).",
      },
    ],
    chemistry: [
      {
        id: "chem-1",
        patientInfo: "72-year-old male with confusion and weakness",
        labResults: [
          { test: "Sodium", value: "118 mEq/L", normal: "135-145 mEq/L", abnormal: true },
          { test: "Potassium", value: "4.2 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: false },
          { test: "Creatinine", value: "0.9 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: false },
          { test: "Glucose", value: "95 mg/dL", normal: "70-100 mg/dL", abnormal: false },
        ],
        additionalInfo: "Recently started HCTZ (diuretic) for hypertension",
        correctAnswer: "Hyponatremia from Diuretic Use",
        options: [
          "Hyponatremia from Diuretic Use",
          "SIADH (Syndrome of Inappropriate ADH)",
          "Acute Kidney Injury",
          "Diabetes Insipidus",
        ],
        explanation: "Severe hyponatremia (low sodium) causing neurological symptoms (confusion) in a patient recently started on thiazide diuretics. Thiazides can cause hyponatremia by increasing sodium excretion. Normal creatinine rules out significant kidney injury.",
      },
      {
        id: "chem-2",
        patientInfo: "45-year-old female with muscle cramps and palpitations",
        labResults: [
          { test: "Potassium", value: "2.8 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Magnesium", value: "1.2 mg/dL", normal: "1.7-2.2 mg/dL", abnormal: true },
          { test: "Sodium", value: "140 mEq/L", normal: "135-145 mEq/L", abnormal: false },
          { test: "Bicarbonate", value: "32 mEq/L", normal: "22-28 mEq/L", abnormal: true },
        ],
        additionalInfo: "History of bulimia with frequent vomiting",
        correctAnswer: "Hypokalemia from Vomiting",
        options: [
          "Hypokalemia from Vomiting",
          "Primary Hyperaldosteronism",
          "Diabetic Ketoacidosis",
          "Renal Tubular Acidosis",
        ],
        explanation: "Hypokalemia (low potassium) with metabolic alkalosis (high bicarbonate) and low magnesium in a patient with frequent vomiting. Vomiting causes loss of gastric acid (HCl) leading to metabolic alkalosis and loss of potassium and magnesium.",
      },
      {
        id: "chem-3",
        patientInfo: "58-year-old male with nausea and decreased urine output",
        labResults: [
          { test: "Creatinine", value: "4.8 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "BUN", value: "68 mg/dL", normal: "7-20 mg/dL", abnormal: true },
          { test: "Potassium", value: "6.2 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Calcium", value: "7.5 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
        ],
        additionalInfo: "Recent CT scan with IV contrast for abdominal pain",
        correctAnswer: "Contrast-Induced Nephropathy",
        options: [
          "Contrast-Induced Nephropathy",
          "Chronic Kidney Disease",
          "Acute Tubular Necrosis",
          "Prerenal Azotemia",
        ],
        explanation: "Acute kidney injury (elevated creatinine and BUN) with hyperkalemia and hypocalcemia following IV contrast exposure. Contrast-induced nephropathy is acute renal failure occurring within 48-72 hours of contrast administration, especially in patients with pre-existing kidney disease or diabetes.",
      },
      {
        id: "chem-4",
        patientInfo: "55-year-old male with jaundice and abdominal pain",
        labResults: [
          { test: "AST", value: "850 U/L", normal: "10-40 U/L", abnormal: true },
          { test: "ALT", value: "920 U/L", normal: "7-56 U/L", abnormal: true },
          { test: "Alkaline Phosphatase", value: "180 U/L", normal: "44-147 U/L", abnormal: true },
          { test: "Total Bilirubin", value: "8.5 mg/dL", normal: "0.1-1.2 mg/dL", abnormal: true },
          { test: "Direct Bilirubin", value: "6.2 mg/dL", normal: "0-0.3 mg/dL", abnormal: true },
        ],
        additionalInfo: "Heavy alcohol use, fever, RUQ tenderness",
        correctAnswer: "Acute Hepatitis (Alcoholic or Viral)",
        options: [
          "Acute Hepatitis (Alcoholic or Viral)",
          "Biliary Obstruction",
          "Cirrhosis",
          "Hemolysis",
        ],
        explanation: "Markedly elevated AST and ALT (>10x normal) with hepatocellular pattern indicates acute hepatitis. AST:ALT ratio >2:1 suggests alcoholic hepatitis. Direct hyperbilirubinemia indicates conjugation is intact but excretion is impaired. Viral hepatitis serologies and abstinence from alcohol are key.",
      },
      {
        id: "chem-5",
        patientInfo: "48-year-old female with bone pain and constipation",
        labResults: [
          { test: "Calcium", value: "12.8 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Phosphorus", value: "2.2 mg/dL", normal: "2.5-4.5 mg/dL", abnormal: true },
          { test: "PTH", value: "145 pg/mL", normal: "15-65 pg/mL", abnormal: true },
          { test: "Creatinine", value: "1.1 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: false },
        ],
        additionalInfo: "Kidney stones x2 in past year, fatigue, depression",
        correctAnswer: "Primary Hyperparathyroidism",
        options: [
          "Primary Hyperparathyroidism",
          "Malignancy-Associated Hypercalcemia",
          "Vitamin D Toxicity",
          "Sarcoidosis",
        ],
        explanation: "Elevated calcium with elevated PTH (inappropriate response) and low phosphorus is classic for primary hyperparathyroidism. 'Bones, stones, groans, and psychiatric moans' describes the symptoms. Usually caused by parathyroid adenoma. Malignancy would have suppressed PTH.",
      },
      {
        id: "chem-6",
        patientInfo: "62-year-old male with altered mental status",
        labResults: [
          { test: "Glucose", value: "45 mg/dL", normal: "70-100 mg/dL", abnormal: true },
          { test: "Sodium", value: "138 mEq/L", normal: "135-145 mEq/L", abnormal: false },
          { test: "Potassium", value: "4.0 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: false },
          { test: "Insulin level", value: "Elevated", normal: "Low when hypoglycemic", abnormal: true },
          { test: "C-peptide", value: "Elevated", normal: "Low when hypoglycemic", abnormal: true },
        ],
        additionalInfo: "Diabetic on sulfonylurea, last meal 12 hours ago",
        correctAnswer: "Drug-Induced Hypoglycemia",
        options: [
          "Drug-Induced Hypoglycemia",
          "Insulinoma",
          "Factitious Hypoglycemia",
          "Adrenal Insufficiency",
        ],
        explanation: "Hypoglycemia with elevated insulin AND elevated C-peptide indicates endogenous insulin production. In a diabetic on sulfonylureas, this is likely drug-induced (sulfonylureas stimulate insulin secretion). Insulinoma would present without diabetes history. Factitious hypoglycemia has elevated insulin but LOW C-peptide.",
      },
      {
        id: "chem-7",
        patientInfo: "70-year-old female with confusion and lethargy",
        labResults: [
          { test: "pH", value: "7.28", normal: "7.35-7.45", abnormal: true },
          { test: "pCO2", value: "24 mmHg", normal: "35-45 mmHg", abnormal: true },
          { test: "HCO3", value: "12 mEq/L", normal: "22-28 mEq/L", abnormal: true },
          { test: "Anion Gap", value: "22", normal: "8-12", abnormal: true },
          { test: "Lactate", value: "8.5 mmol/L", normal: "<2 mmol/L", abnormal: true },
        ],
        additionalInfo: "Found on floor at home, hypotensive, recent pneumonia",
        correctAnswer: "Lactic Acidosis (Septic Shock)",
        options: [
          "Lactic Acidosis (Septic Shock)",
          "Diabetic Ketoacidosis",
          "Uremic Acidosis",
          "Respiratory Acidosis",
        ],
        explanation: "High anion gap metabolic acidosis (low pH, low HCO3) with respiratory compensation (low pCO2) and markedly elevated lactate indicates lactic acidosis. In setting of hypotension and infection, this is septic shock. Type A lactic acidosis from tissue hypoperfusion.",
      },
      {
        id: "chem-8",
        patientInfo: "65-year-old male with jaundice and itching",
        labResults: [
          { test: "Alkaline Phosphatase", value: "520 U/L", normal: "44-147 U/L", abnormal: true },
          { test: "GGT", value: "380 U/L", normal: "0-51 U/L", abnormal: true },
          { test: "AST", value: "85 U/L", normal: "10-40 U/L", abnormal: true },
          { test: "ALT", value: "92 U/L", normal: "7-56 U/L", abnormal: true },
          { test: "Direct Bilirubin", value: "8.5 mg/dL", normal: "0-0.3 mg/dL", abnormal: true },
        ],
        additionalInfo: "Ultrasound shows dilated bile ducts, weight loss",
        correctAnswer: "Biliary Obstruction (Cholestasis)",
        options: [
          "Biliary Obstruction (Cholestasis)",
          "Acute Hepatitis",
          "Cirrhosis",
          "Gilbert Syndrome",
        ],
        explanation: "Markedly elevated alkaline phosphatase and GGT with only mildly elevated transaminases indicates cholestatic (obstructive) pattern. Direct hyperbilirubinemia and dilated ducts confirm obstruction. Causes include gallstones, pancreatic cancer, or stricture.",
      },
      {
        id: "chem-9",
        patientInfo: "78-year-old female with weakness and muscle aches",
        labResults: [
          { test: "CK (Creatine Kinase)", value: "12,500 U/L", normal: "55-170 U/L", abnormal: true },
          { test: "Creatinine", value: "3.2 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "Potassium", value: "6.8 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Myoglobin (urine)", value: "Positive", normal: "Negative", abnormal: true },
        ],
        additionalInfo: "Found on floor after fall, unable to get up for 2 days",
        correctAnswer: "Rhabdomyolysis",
        options: [
          "Rhabdomyolysis",
          "Myocardial Infarction",
          "Polymyositis",
          "Muscular Dystrophy",
        ],
        explanation: "Massively elevated CK (>10,000) with AKI, hyperkalemia, and myoglobinuria after prolonged immobilization indicates rhabdomyolysis. Muscle breakdown releases myoglobin which is toxic to kidneys. Requires aggressive IV fluids.",
      },
      {
        id: "chem-10",
        patientInfo: "55-year-old male with chronic fatigue",
        labResults: [
          { test: "Creatinine", value: "4.2 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "BUN", value: "58 mg/dL", normal: "7-20 mg/dL", abnormal: true },
          { test: "Hemoglobin", value: "9.5 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "Calcium", value: "7.8 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Phosphorus", value: "6.5 mg/dL", normal: "2.5-4.5 mg/dL", abnormal: true },
        ],
        additionalInfo: "Diabetic for 20 years, proteinuria on urine test",
        correctAnswer: "Chronic Kidney Disease (Diabetic Nephropathy)",
        options: [
          "Chronic Kidney Disease (Diabetic Nephropathy)",
          "Acute Kidney Injury",
          "Glomerulonephritis",
          "Polycystic Kidney Disease",
        ],
        explanation: "Elevated creatinine and BUN with anemia (from low EPO), hypocalcemia, and hyperphosphatemia is classic for CKD. Long-standing diabetes with proteinuria points to diabetic nephropathy as the cause. eGFR would confirm stage of CKD.",
      },
      {
        id: "chem-11",
        patientInfo: "30-year-old male found unresponsive at a party",
        labResults: [
          { test: "pH", value: "7.15", normal: "7.35-7.45", abnormal: true },
          { test: "pCO2", value: "20 mmHg", normal: "35-45 mmHg", abnormal: true },
          { test: "HCO3", value: "8 mEq/L", normal: "22-28 mEq/L", abnormal: true },
          { test: "Anion Gap", value: "28", normal: "8-12", abnormal: true },
          { test: "Osmolar Gap", value: "25 mOsm/kg", normal: "<10 mOsm/kg", abnormal: true },
        ],
        additionalInfo: "Visual complaints before losing consciousness, recent breakup",
        correctAnswer: "Toxic Alcohol Ingestion (Methanol or Ethylene Glycol)",
        options: [
          "Toxic Alcohol Ingestion (Methanol or Ethylene Glycol)",
          "Diabetic Ketoacidosis",
          "Lactic Acidosis",
          "Ethanol Intoxication",
        ],
        explanation: "High anion gap metabolic acidosis with elevated osmolar gap suggests toxic alcohol ingestion. Visual symptoms point to methanol (metabolized to formic acid which damages optic nerve). Requires fomepizole and possibly hemodialysis.",
      },
      {
        id: "chem-12",
        patientInfo: "68-year-old female with confusion, weakness, and constipation",
        labResults: [
          { test: "Calcium", value: "14.2 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "PTH", value: "8 pg/mL", normal: "15-65 pg/mL", abnormal: true },
          { test: "PTHrP", value: "Elevated", normal: "Undetectable", abnormal: true },
          { test: "Creatinine", value: "1.8 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
        ],
        additionalInfo: "30-pack-year smoking history, recent weight loss, chest X-ray shows lung mass",
        correctAnswer: "Hypercalcemia of Malignancy (PTHrP)",
        options: [
          "Hypercalcemia of Malignancy (PTHrP)",
          "Primary Hyperparathyroidism",
          "Vitamin D Toxicity",
          "Milk-Alkali Syndrome",
        ],
        explanation: "Severe hypercalcemia with SUPPRESSED PTH and elevated PTHrP indicates humoral hypercalcemia of malignancy. Squamous cell lung cancer commonly produces PTHrP. Symptoms follow 'stones, bones, groans, moans' pattern. Treatment: IV fluids, bisphosphonates, and treat underlying cancer.",
      },
      {
        id: "chem-13",
        patientInfo: "45-year-old male with severe muscle weakness and cardiac arrhythmia",
        labResults: [
          { test: "Potassium", value: "7.2 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Creatinine", value: "1.0 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: false },
          { test: "Glucose", value: "85 mg/dL", normal: "70-100 mg/dL", abnormal: false },
          { test: "Bicarbonate", value: "18 mEq/L", normal: "22-28 mEq/L", abnormal: true },
          { test: "Cortisol (AM)", value: "3.2 μg/dL", normal: "7-25 μg/dL", abnormal: true },
        ],
        additionalInfo: "Recently stopped taking prednisone after long-term use, hypotension",
        correctAnswer: "Adrenal Crisis (Steroid Withdrawal)",
        options: [
          "Adrenal Crisis (Steroid Withdrawal)",
          "Acute Kidney Injury",
          "Tumor Lysis Syndrome",
          "Medication-Induced Hyperkalemia",
        ],
        explanation: "Hyperkalemia with low cortisol after stopping chronic steroids indicates adrenal crisis from HPA axis suppression. Long-term steroids suppress ACTH; abrupt discontinuation causes adrenal insufficiency. Life-threatening emergency requiring immediate IV hydrocortisone.",
      },
      {
        id: "chem-14",
        patientInfo: "22-year-old female with fatigue and muscle cramps",
        labResults: [
          { test: "Potassium", value: "2.5 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Magnesium", value: "1.0 mg/dL", normal: "1.7-2.2 mg/dL", abnormal: true },
          { test: "Calcium", value: "8.0 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "pH", value: "7.48", normal: "7.35-7.45", abnormal: true },
          { test: "Bicarbonate", value: "32 mEq/L", normal: "22-28 mEq/L", abnormal: true },
        ],
        additionalInfo: "Using laxatives and diuretics for weight loss, dental enamel erosion",
        correctAnswer: "Electrolyte Abnormalities from Purging Behavior",
        options: [
          "Electrolyte Abnormalities from Purging Behavior",
          "Bartter Syndrome",
          "Gitelman Syndrome",
          "Primary Hyperaldosteronism",
        ],
        explanation: "Hypokalemia, hypomagnesemia, metabolic alkalosis in young female with laxative/diuretic abuse and dental erosion suggests eating disorder with purging behavior. Refractory hypokalemia requires magnesium repletion first. Screen for eating disorder.",
      },
      {
        id: "chem-15",
        patientInfo: "60-year-old male with acute leukemia starting chemotherapy",
        labResults: [
          { test: "Potassium", value: "6.8 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Phosphorus", value: "8.5 mg/dL", normal: "2.5-4.5 mg/dL", abnormal: true },
          { test: "Calcium", value: "6.2 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Uric Acid", value: "14.5 mg/dL", normal: "3.5-7.2 mg/dL", abnormal: true },
          { test: "LDH", value: "2500 U/L", normal: "140-280 U/L", abnormal: true },
        ],
        additionalInfo: "Day 2 of chemotherapy, decreased urine output",
        correctAnswer: "Tumor Lysis Syndrome",
        options: [
          "Tumor Lysis Syndrome",
          "Acute Kidney Injury",
          "Rhabdomyolysis",
          "Severe Sepsis",
        ],
        explanation: "Classic tumor lysis syndrome: hyperkalemia, hyperphosphatemia, hypocalcemia (from phosphate binding), and hyperuricemia after chemotherapy. Massive cell death releases intracellular contents. Prevention with allopurinol/rasburicase and aggressive hydration is key.",
      },
      {
        id: "chem-16",
        patientInfo: "52-year-old male with chronic alcoholism and confusion",
        labResults: [
          { test: "Sodium", value: "122 mEq/L", normal: "135-145 mEq/L", abnormal: true },
          { test: "Glucose", value: "75 mg/dL", normal: "70-100 mg/dL", abnormal: false },
          { test: "Serum Osmolality", value: "255 mOsm/kg", normal: "275-295 mOsm/kg", abnormal: true },
          { test: "Urine Sodium", value: "55 mEq/L", normal: "<20 mEq/L (hypovolemic)", abnormal: true },
          { test: "Urine Osmolality", value: "620 mOsm/kg", normal: "<100 (if diluting)", abnormal: true },
        ],
        additionalInfo: "Euvolemic on exam, no edema, no signs of dehydration",
        correctAnswer: "Beer Potomania (SIADH-like)",
        options: [
          "Beer Potomania (SIADH-like)",
          "Psychogenic Polydipsia",
          "Congestive Heart Failure",
          "Cirrhosis with Ascites",
        ],
        explanation: "Euvolemic hyponatremia with concentrated urine in chronic alcoholic suggests beer potomania. Low solute intake (beer has few electrolytes) limits water excretion capacity. Unlike SIADH, can correct with normal diet. Correct slowly to avoid osmotic demyelination.",
      },
      {
        id: "chem-17",
        patientInfo: "35-year-old female with recurrent kidney stones",
        labResults: [
          { test: "Calcium", value: "9.2 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: false },
          { test: "PTH", value: "18 pg/mL", normal: "15-65 pg/mL", abnormal: false },
          { test: "24-hr Urine Calcium", value: "450 mg", normal: "<300 mg", abnormal: true },
          { test: "24-hr Urine Citrate", value: "180 mg", normal: ">320 mg", abnormal: true },
          { test: "Urine pH", value: "5.2", normal: "5.5-7.0", abnormal: true },
        ],
        additionalInfo: "3 calcium oxalate stones in past 2 years, high-protein diet",
        correctAnswer: "Idiopathic Hypercalciuria with Hypocitraturia",
        options: [
          "Idiopathic Hypercalciuria with Hypocitraturia",
          "Primary Hyperparathyroidism",
          "Renal Tubular Acidosis Type 1",
          "Cystinuria",
        ],
        explanation: "Normal serum calcium with elevated 24-hour urine calcium indicates idiopathic hypercalciuria. Low citrate (citrate inhibits stone formation) and acidic urine increase stone risk. Treatment: thiazide diuretics (reduce urine calcium), potassium citrate, and dietary modification.",
      },
      {
        id: "chem-18",
        patientInfo: "70-year-old male with new confusion and asterixis",
        labResults: [
          { test: "Ammonia", value: "145 μmol/L", normal: "15-45 μmol/L", abnormal: true },
          { test: "AST", value: "65 U/L", normal: "10-40 U/L", abnormal: true },
          { test: "ALT", value: "48 U/L", normal: "7-56 U/L", abnormal: false },
          { test: "Albumin", value: "2.5 g/dL", normal: "3.5-5.0 g/dL", abnormal: true },
          { test: "INR", value: "1.8", normal: "0.8-1.1", abnormal: true },
        ],
        additionalInfo: "History of cirrhosis, recent GI bleed requiring transfusion",
        correctAnswer: "Hepatic Encephalopathy",
        options: [
          "Hepatic Encephalopathy",
          "Acute Liver Failure",
          "Wernicke Encephalopathy",
          "Uremic Encephalopathy",
        ],
        explanation: "Elevated ammonia with asterixis (flapping tremor) in cirrhotic patient indicates hepatic encephalopathy. GI bleed is common precipitant (protein load from blood increases ammonia). Low albumin and elevated INR show chronic liver dysfunction. Treatment: lactulose, rifaximin.",
      },
      {
        id: "chem-19",
        patientInfo: "28-year-old female with severe diarrhea for 5 days",
        labResults: [
          { test: "pH", value: "7.28", normal: "7.35-7.45", abnormal: true },
          { test: "Bicarbonate", value: "14 mEq/L", normal: "22-28 mEq/L", abnormal: true },
          { test: "pCO2", value: "28 mmHg", normal: "35-45 mmHg", abnormal: true },
          { test: "Anion Gap", value: "10", normal: "8-12", abnormal: false },
          { test: "Potassium", value: "2.8 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
        ],
        additionalInfo: "Traveler's diarrhea, 10+ watery stools per day",
        correctAnswer: "Non-Anion Gap Metabolic Acidosis (GI Bicarbonate Loss)",
        options: [
          "Non-Anion Gap Metabolic Acidosis (GI Bicarbonate Loss)",
          "Diabetic Ketoacidosis",
          "Lactic Acidosis",
          "Renal Tubular Acidosis",
        ],
        explanation: "Metabolic acidosis (low pH, low HCO3) with NORMAL anion gap and hypokalemia from diarrhea. GI losses below the pylorus lose bicarbonate-rich fluid. This is non-anion gap (hyperchloremic) metabolic acidosis. Treatment: IV fluids with bicarbonate and potassium replacement.",
      },
      {
        id: "chem-20",
        patientInfo: "58-year-old male with cirrhosis presenting with abdominal distension",
        labResults: [
          { test: "Sodium", value: "126 mEq/L", normal: "135-145 mEq/L", abnormal: true },
          { test: "Creatinine", value: "2.8 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "BUN", value: "45 mg/dL", normal: "7-20 mg/dL", abnormal: true },
          { test: "Urine Sodium", value: "<10 mEq/L", normal: ">20 mEq/L", abnormal: true },
          { test: "Albumin", value: "2.2 g/dL", normal: "3.5-5.0 g/dL", abnormal: true },
        ],
        additionalInfo: "Tense ascites, no response to diuretics, no nephrotoxic drug exposure",
        correctAnswer: "Hepatorenal Syndrome",
        options: [
          "Hepatorenal Syndrome",
          "Prerenal Azotemia",
          "Acute Tubular Necrosis",
          "Contrast-Induced Nephropathy",
        ],
        explanation: "Progressive kidney failure in advanced cirrhosis with very low urine sodium (<10) suggests hepatorenal syndrome. Splanchnic vasodilation leads to decreased effective arterial volume and renal vasoconstriction. Poor prognosis without liver transplant. Terlipressin + albumin may help.",
      },
      {
        id: "chem-21",
        patientInfo: "42-year-old female with weakness and tingling around mouth",
        labResults: [
          { test: "Calcium", value: "6.8 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Magnesium", value: "0.9 mg/dL", normal: "1.7-2.2 mg/dL", abnormal: true },
          { test: "Potassium", value: "2.9 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "PTH", value: "85 pg/mL", normal: "15-65 pg/mL", abnormal: true },
          { test: "Albumin", value: "4.0 g/dL", normal: "3.5-5.0 g/dL", abnormal: false },
        ],
        additionalInfo: "History of Crohn's disease with multiple small bowel resections, chronic diarrhea",
        correctAnswer: "Severe Hypomagnesemia with Secondary Hypocalcemia",
        options: [
          "Severe Hypomagnesemia with Secondary Hypocalcemia",
          "Primary Hypoparathyroidism",
          "Vitamin D Deficiency",
          "Pseudohypoparathyroidism",
        ],
        explanation: "Severe hypomagnesemia causes hypocalcemia through two mechanisms: impaired PTH secretion and PTH resistance. Elevated PTH shows the glands are responding but tissues are resistant. Common in malabsorption syndromes. Must replace magnesium first; calcium won't correct until magnesium is normal.",
      },
      {
        id: "chem-22",
        patientInfo: "28-year-old male collapsed after a marathon in extreme heat, presenting with dark brown urine and oliguria",
        labResults: [
          { test: "CK (Creatine Kinase)", value: "85,400 U/L", normal: "<200 U/L", abnormal: true },
          { test: "Creatinine", value: "3.8 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "Potassium", value: "6.8 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Phosphate", value: "6.2 mg/dL", normal: "2.5-4.5 mg/dL", abnormal: true },
          { test: "Calcium", value: "6.8 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Uric Acid", value: "12.4 mg/dL", normal: "3.5-7.2 mg/dL", abnormal: true },
        ],
        additionalInfo: "Urine dipstick: 3+ blood with NO red blood cells on microscopy (myoglobinuria). Compartment syndrome in left lower leg. ECG: peaked T waves.",
        correctAnswer: "Rhabdomyolysis with Acute Kidney Injury",
        options: [
          "Rhabdomyolysis with Acute Kidney Injury",
          "Haemoglobinuria",
          "Acute Tubular Necrosis",
          "Tumour Lysis Syndrome",
        ],
        explanation: "Rhabdomyolysis: muscle breakdown releases myoglobin → direct tubular toxicity + cast formation → AKI. KEY: urine dipstick blood-positive with NO RBCs on microscopy = myoglobinuria (or haemoglobinuria). CK massively elevated (typically >5× ULN, often >10,000). Hyperkalaemia (released from muscle), hypocalcaemia (calcium deposited in damaged muscle — do not correct unless symptomatic as it rebounds during recovery), hyperphosphataemia, hyperuricaemia. Treatment: aggressive IV fluids (target urine output 200-300 mL/hr), urinary alkalinisation controversial.",
      },
      {
        id: "chem-23",
        patientInfo: "22-year-old male with Burkitt lymphoma, 48 hours after starting chemotherapy, developing muscle cramps and ECG changes",
        labResults: [
          { test: "Uric Acid", value: "18.6 mg/dL", normal: "3.5-7.2 mg/dL", abnormal: true },
          { test: "Potassium", value: "6.9 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Phosphate", value: "7.8 mg/dL", normal: "2.5-4.5 mg/dL", abnormal: true },
          { test: "Calcium", value: "6.2 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Creatinine", value: "4.2 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "LDH", value: "4,800 U/L", normal: "140-280 U/L", abnormal: true },
        ],
        additionalInfo: "Rasburicase prophylaxis was started late. ECG shows peaked T waves and widened QRS. Muscle cramps and Trousseau's sign positive.",
        correctAnswer: "Tumour Lysis Syndrome",
        options: [
          "Tumour Lysis Syndrome",
          "Rhabdomyolysis",
          "Acute Kidney Injury from Contrast",
          "Primary Hyperparathyroidism",
        ],
        explanation: "Tumour Lysis Syndrome (TLS): rapid cancer cell death releases intracellular contents. Cairo-Bishop criteria: ≥2 of: uric acid ≥8, K ≥6, phosphate ≥4.5 mg/dL, calcium ≤7 mg/dL (or ≥25% change). Elevated LDH = marker of cell turnover. Hypocalcaemia from calcium-phosphate precipitation (do not give calcium unless symptomatic tetany — worsens tissue calcification). Life-threatening hyperkalaemia causes arrhythmias. Prevention: IV fluids, allopurinol or rasburicase (converts uric acid to allantoin — 10× more soluble) prophylactically.",
      },
      {
        id: "chem-24",
        patientInfo: "19-year-old female with tetany, carpopedal spasm, and muscle weakness, BMI 16.5",
        labResults: [
          { test: "pH (arterial)", value: "7.55", normal: "7.35-7.45", abnormal: true },
          { test: "HCO3", value: "36 mEq/L", normal: "22-28 mEq/L", abnormal: true },
          { test: "pCO2", value: "48 mmHg", normal: "35-45 mmHg", abnormal: true },
          { test: "Chloride", value: "88 mEq/L", normal: "98-106 mEq/L", abnormal: true },
          { test: "Potassium", value: "2.6 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Urine Chloride", value: "12 mEq/L", normal: ">20 mEq/L", abnormal: true },
        ],
        additionalInfo: "Dental erosions, bilateral parotid enlargement, and calluses on dorsal knuckles (Russell's sign). Positive Trousseau's sign.",
        correctAnswer: "Metabolic Alkalosis from Purging (Bulimia Nervosa)",
        options: [
          "Metabolic Alkalosis from Purging (Bulimia Nervosa)",
          "Conn's Syndrome",
          "Bartter Syndrome",
          "Milk-Alkali Syndrome",
        ],
        explanation: "Purging causes loss of HCl → metabolic alkalosis with hypochloraemia and hypokalaemia. Respiratory compensation raises pCO2. Urine Cl <20 mEq/L = 'saline-responsive' alkalosis (volume depleted, kidneys avid for Cl; correct with IV NaCl + KCl). Conn's and Bartter syndromes are saline-resistant (urine Cl >40 mEq/L). Russell's sign, dental erosions, and parotid hypertrophy suggest bulimia. Tetany from alkalosis (ionised calcium falls with rising pH).",
      },
      {
        id: "chem-25",
        patientInfo: "52-year-old male with jaundice, ascites, and confusion, chronic alcohol excess (>15 units/day for 20 years)",
        labResults: [
          { test: "AST", value: "280 U/L", normal: "<40 U/L", abnormal: true },
          { test: "ALT", value: "95 U/L", normal: "<40 U/L", abnormal: true },
          { test: "GGT", value: "850 U/L", normal: "<50 U/L", abnormal: true },
          { test: "Bilirubin (Total)", value: "8.2 mg/dL", normal: "<1.2 mg/dL", abnormal: true },
          { test: "Albumin", value: "2.3 g/dL", normal: "3.5-5.0 g/dL", abnormal: true },
          { test: "PT/INR", value: "1.9", normal: "0.9-1.1", abnormal: true },
          { test: "Platelets", value: "88,000/μL", normal: "150,000-400,000/μL", abnormal: true },
        ],
        additionalInfo: "Spider naevi, palmar erythema, parotid enlargement, Dupuytren's contracture. Ascites confirmed on ultrasound. Sodium 128 mEq/L.",
        correctAnswer: "Alcoholic Liver Disease with Cirrhosis",
        options: [
          "Alcoholic Liver Disease with Cirrhosis",
          "Acute Viral Hepatitis",
          "Non-Alcoholic Steatohepatitis",
          "Autoimmune Hepatitis",
        ],
        explanation: "Classic alcoholic liver disease: AST:ALT ratio >2:1 (alcohol suppresses pyridoxal phosphate, a cofactor mainly needed for ALT synthesis). Markedly elevated GGT = most sensitive marker of alcohol use, even with mild liver damage. Thrombocytopenia = hypersplenism + direct marrow suppression. Low albumin + prolonged PT = hepatic synthetic failure. Dilutional hyponatraemia from cirrhosis. Maddrey's discriminant function (AST + bilirubin) predicts 28-day mortality in alcoholic hepatitis.",
      },
      {
        id: "chem-26",
        patientInfo: "78-year-old female with type 2 diabetes on metformin, presenting with nausea, vomiting, and confusion after AKI from dehydration",
        labResults: [
          { test: "pH (arterial)", value: "7.19", normal: "7.35-7.45", abnormal: true },
          { test: "Lactate", value: "8.4 mmol/L", normal: "<2.0 mmol/L", abnormal: true },
          { test: "HCO3", value: "11 mEq/L", normal: "22-28 mEq/L", abnormal: true },
          { test: "Anion Gap", value: "28", normal: "8-12", abnormal: true },
          { test: "Creatinine", value: "4.6 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "Glucose", value: "148 mg/dL", normal: "70-100 mg/dL", abnormal: true },
          { test: "Ketones (urine)", value: "Negative", normal: "Negative", abnormal: false },
        ],
        additionalInfo: "Recent contrast CT scan. Metformin dose not withheld despite elevated creatinine. No sepsis source identified.",
        correctAnswer: "Metformin-Associated Lactic Acidosis",
        options: [
          "Metformin-Associated Lactic Acidosis",
          "Diabetic Ketoacidosis",
          "Septic Shock",
          "Alcoholic Ketoacidosis",
        ],
        explanation: "Metformin accumulates in renal impairment and inhibits mitochondrial complex I → impaired hepatic lactate clearance → Type B lactic acidosis (not due to tissue hypoxia). Raised anion gap acidosis + high lactate + metformin + AKI is the classic triad. No ketones distinguishes from DKA. Metformin is contraindicated when eGFR <30 and should be held for contrast procedures. Management: stop metformin, aggressive fluids, CVVHF/dialysis for severe cases (lactate >10 or pH <7.1).",
      },
    ],
    cardiac: [
      {
        id: "cardiac-1",
        patientInfo: "62-year-old male with chest pain for 3 hours",
        labResults: [
          { test: "Troponin I", value: "8.5 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "CK-MB", value: "42 ng/mL", normal: "<5 ng/mL", abnormal: true },
          { test: "Myoglobin", value: "180 ng/mL", normal: "<90 ng/mL", abnormal: true },
          { test: "BNP", value: "85 pg/mL", normal: "<100 pg/mL", abnormal: false },
        ],
        additionalInfo: "ECG shows ST elevation in leads V2-V4",
        correctAnswer: "Acute Myocardial Infarction (STEMI)",
        options: [
          "Acute Myocardial Infarction (STEMI)",
          "Unstable Angina",
          "Myocarditis",
          "Pulmonary Embolism",
        ],
        explanation: "Markedly elevated troponin and CK-MB with ST elevations on ECG indicates acute STEMI (ST-elevation myocardial infarction). Troponin is the most specific cardiac marker. Rising myoglobin is an early marker. This patient needs immediate cardiac catheterization.",
      },
      {
        id: "cardiac-2",
        patientInfo: "70-year-old female with shortness of breath and leg swelling",
        labResults: [
          { test: "BNP", value: "1,250 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "Troponin I", value: "0.02 ng/mL", normal: "<0.04 ng/mL", abnormal: false },
          { test: "Creatinine", value: "1.4 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
        ],
        additionalInfo: "Crackles on lung exam, elevated JVP",
        correctAnswer: "Acute Decompensated Heart Failure",
        options: [
          "Acute Decompensated Heart Failure",
          "Acute Myocardial Infarction",
          "Pulmonary Embolism",
          "Pneumonia",
        ],
        explanation: "Markedly elevated BNP (B-type natriuretic peptide) with normal troponin in a patient with dyspnea, edema, and volume overload signs indicates heart failure. BNP >400 pg/mL strongly suggests heart failure. Elevated creatinine may indicate cardiorenal syndrome.",
      },
      {
        id: "cardiac-3",
        patientInfo: "55-year-old male with no symptoms, routine screening",
        labResults: [
          { test: "Total Cholesterol", value: "285 mg/dL", normal: "<200 mg/dL", abnormal: true },
          { test: "LDL", value: "190 mg/dL", normal: "<100 mg/dL", abnormal: true },
          { test: "HDL", value: "35 mg/dL", normal: ">40 mg/dL", abnormal: true },
          { test: "Triglycerides", value: "300 mg/dL", normal: "<150 mg/dL", abnormal: true },
        ],
        additionalInfo: "Family history of early heart disease, diabetes",
        correctAnswer: "Severe Hyperlipidemia",
        options: [
          "Severe Hyperlipidemia",
          "Familial Hypercholesterolemia",
          "Metabolic Syndrome",
          "Normal Lipid Panel",
        ],
        explanation: "Severely elevated total cholesterol, LDL (bad cholesterol), and triglycerides with low HDL (good cholesterol). This patient has very high cardiovascular risk and requires statin therapy. The pattern with diabetes and high triglycerides suggests metabolic syndrome, but the severe LDL elevation is the most concerning finding requiring immediate treatment.",
      },
      {
        id: "cardiac-4",
        patientInfo: "48-year-old female with sudden shortness of breath",
        labResults: [
          { test: "D-dimer", value: "4,500 ng/mL", normal: "<500 ng/mL", abnormal: true },
          { test: "Troponin I", value: "0.15 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "BNP", value: "450 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "ABG pH", value: "7.48", normal: "7.35-7.45", abnormal: true },
        ],
        additionalInfo: "Recent hip surgery 1 week ago, pleuritic chest pain, tachycardia",
        correctAnswer: "Pulmonary Embolism",
        options: [
          "Pulmonary Embolism",
          "Myocardial Infarction",
          "Heart Failure",
          "Pneumonia",
        ],
        explanation: "Markedly elevated D-dimer with recent immobility (surgery), pleuritic chest pain, and respiratory alkalosis (hyperventilation) strongly suggests PE. Elevated troponin and BNP indicate right heart strain from PE. CT angiogram is needed to confirm.",
      },
      {
        id: "cardiac-5",
        patientInfo: "35-year-old male runner with chest pain after marathon",
        labResults: [
          { test: "Troponin I", value: "0.08 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "CK-MB", value: "8 ng/mL", normal: "<5 ng/mL", abnormal: true },
          { test: "CK Total", value: "2,500 U/L", normal: "55-170 U/L", abnormal: true },
          { test: "BNP", value: "65 pg/mL", normal: "<100 pg/mL", abnormal: false },
        ],
        additionalInfo: "ECG shows diffuse ST changes, recent viral illness",
        correctAnswer: "Myocarditis",
        options: [
          "Myocarditis",
          "Acute Myocardial Infarction",
          "Exercise-Induced Troponin Elevation",
          "Rhabdomyolysis",
        ],
        explanation: "Mildly elevated troponin with very high CK after strenuous exercise could be exercise-induced, but diffuse ST changes and recent viral illness suggest myocarditis. Young age makes MI less likely. Echocardiogram would show wall motion abnormalities in myocarditis.",
      },
      {
        id: "cardiac-6",
        patientInfo: "65-year-old male with palpitations and near-syncope",
        labResults: [
          { test: "Troponin I", value: "0.01 ng/mL", normal: "<0.04 ng/mL", abnormal: false },
          { test: "BNP", value: "180 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "Potassium", value: "2.6 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Magnesium", value: "1.1 mg/dL", normal: "1.7-2.2 mg/dL", abnormal: true },
        ],
        additionalInfo: "On diuretics, ECG shows prolonged QT and U waves",
        correctAnswer: "Hypokalemia-Induced Arrhythmia",
        options: [
          "Hypokalemia-Induced Arrhythmia",
          "Atrial Fibrillation",
          "Acute Coronary Syndrome",
          "Digoxin Toxicity",
        ],
        explanation: "Severe hypokalemia and hypomagnesemia in patient on diuretics with QT prolongation and U waves. Electrolyte abnormalities cause dangerous arrhythmias. Must replace potassium and magnesium carefully with cardiac monitoring.",
      },
      {
        id: "cardiac-7",
        patientInfo: "72-year-old female with progressive dyspnea over 2 weeks",
        labResults: [
          { test: "BNP", value: "2,800 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "Troponin I", value: "0.06 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "Creatinine", value: "2.4 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "Hemoglobin", value: "9.2 g/dL", normal: "12-16 g/dL", abnormal: true },
        ],
        additionalInfo: "History of CHF, bilateral leg edema, orthopnea, medication non-compliance",
        correctAnswer: "Acute on Chronic Heart Failure Exacerbation",
        options: [
          "Acute on Chronic Heart Failure Exacerbation",
          "Acute Myocardial Infarction",
          "Chronic Kidney Disease",
          "Severe Anemia",
        ],
        explanation: "Very high BNP (>1000 strongly suggests heart failure) with volume overload symptoms, mildly elevated troponin (demand ischemia), and cardiorenal syndrome (elevated creatinine from poor cardiac output). Non-compliance with medications is a common precipitant of CHF exacerbation.",
      },
      {
        id: "cardiac-8",
        patientInfo: "58-year-old male with chest pain relieved by leaning forward",
        labResults: [
          { test: "Troponin I", value: "0.12 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "ESR", value: "65 mm/hr", normal: "0-22 mm/hr", abnormal: true },
          { test: "CRP", value: "8.5 mg/dL", normal: "<1 mg/dL", abnormal: true },
          { test: "WBC", value: "12,000/μL", normal: "4,000-11,000/μL", abnormal: true },
        ],
        additionalInfo: "ECG shows diffuse ST elevation and PR depression, friction rub on exam",
        correctAnswer: "Acute Pericarditis",
        options: [
          "Acute Pericarditis",
          "Acute Myocardial Infarction",
          "Myocarditis",
          "Aortic Dissection",
        ],
        explanation: "Pleuritic chest pain relieved by leaning forward, friction rub, diffuse ST elevation with PR depression, and elevated inflammatory markers (ESR, CRP) are classic for acute pericarditis. Unlike MI, ST elevations are diffuse and not in coronary distribution.",
      },
      {
        id: "cardiac-9",
        patientInfo: "45-year-old male on digoxin for atrial fibrillation",
        labResults: [
          { test: "Digoxin level", value: "3.2 ng/mL", normal: "0.8-2.0 ng/mL", abnormal: true },
          { test: "Potassium", value: "3.0 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Creatinine", value: "2.1 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "BUN", value: "35 mg/dL", normal: "7-20 mg/dL", abnormal: true },
        ],
        additionalInfo: "Nausea, visual disturbances (yellow-green halos), bradycardia",
        correctAnswer: "Digoxin Toxicity",
        options: [
          "Digoxin Toxicity",
          "Sick Sinus Syndrome",
          "Complete Heart Block",
          "Hyperkalemia",
        ],
        explanation: "Elevated digoxin level with classic toxicity symptoms (GI upset, visual changes, bradycardia). Hypokalemia and renal impairment increase digoxin toxicity risk. Treatment includes stopping digoxin, correcting potassium, and digoxin immune fab for severe cases.",
      },
      {
        id: "cardiac-10",
        patientInfo: "60-year-old female with sudden severe chest pain radiating to back",
        labResults: [
          { test: "Troponin I", value: "0.02 ng/mL", normal: "<0.04 ng/mL", abnormal: false },
          { test: "D-dimer", value: "2,800 ng/mL", normal: "<500 ng/mL", abnormal: true },
          { test: "Creatinine", value: "1.0 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: false },
          { test: "Lactate", value: "1.5 mmol/L", normal: "<2 mmol/L", abnormal: false },
        ],
        additionalInfo: "BP 180/100 right arm, 140/80 left arm, history of hypertension, widened mediastinum on CXR",
        correctAnswer: "Aortic Dissection",
        options: [
          "Aortic Dissection",
          "Pulmonary Embolism",
          "Acute MI",
          "Esophageal Rupture",
        ],
        explanation: "Tearing chest pain radiating to back, blood pressure differential between arms, and widened mediastinum strongly suggest aortic dissection. Elevated D-dimer is common. Normal troponin helps exclude MI. CT angiogram is diagnostic. This is a surgical emergency.",
      },
      {
        id: "cardiac-11",
        patientInfo: "70-year-old male on warfarin presenting with bleeding",
        labResults: [
          { test: "INR", value: "8.5", normal: "2.0-3.0 (therapeutic)", abnormal: true },
          { test: "PT", value: "85 seconds", normal: "11-13.5 seconds", abnormal: true },
          { test: "Hemoglobin", value: "8.5 g/dL", normal: "14-18 g/dL", abnormal: true },
          { test: "Platelets", value: "185,000/μL", normal: "150,000-400,000/μL", abnormal: false },
        ],
        additionalInfo: "Recent antibiotics for tooth infection, gum bleeding and large bruises",
        correctAnswer: "Warfarin Over-Anticoagulation",
        options: [
          "Warfarin Over-Anticoagulation",
          "Vitamin K Deficiency",
          "Liver Disease",
          "DIC",
        ],
        explanation: "Supratherapeutic INR in patient on warfarin with bleeding signs. Antibiotics (especially metronidazole, fluoroquinolones) inhibit warfarin metabolism, potentiating its effect. Treatment: hold warfarin, give vitamin K, and consider PCC or FFP for serious bleeding.",
      },
      {
        id: "cardiac-12",
        patientInfo: "58-year-old male with sudden severe chest pain radiating to the back",
        labResults: [
          { test: "Troponin I", value: "0.08 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "D-dimer", value: "4,500 ng/mL", normal: "<500 ng/mL", abnormal: true },
          { test: "Creatinine", value: "1.8 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "Lactate", value: "4.2 mmol/L", normal: "0.5-2.0 mmol/L", abnormal: true },
        ],
        additionalInfo: "History of hypertension, BP 185/110 right arm vs 140/85 left arm, widened mediastinum on CXR",
        correctAnswer: "Aortic Dissection",
        options: [
          "Aortic Dissection",
          "Acute MI",
          "Pulmonary Embolism",
          "Esophageal Rupture",
        ],
        explanation: "Blood pressure differential between arms, tearing chest pain radiating to back, widened mediastinum, and elevated D-dimer are classic for aortic dissection. Mild troponin elevation can occur if dissection involves coronary ostia. CT angiography confirms diagnosis.",
      },
      {
        id: "cardiac-13",
        patientInfo: "72-year-old female with progressive dyspnea and leg swelling",
        labResults: [
          { test: "BNP", value: "1,850 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "Troponin I", value: "0.02 ng/mL", normal: "<0.04 ng/mL", abnormal: false },
          { test: "Creatinine", value: "1.6 mg/dL", normal: "0.5-1.1 mg/dL", abnormal: true },
          { test: "Sodium", value: "132 mEq/L", normal: "135-145 mEq/L", abnormal: true },
        ],
        additionalInfo: "History of HTN and diabetes, bilateral crackles, JVD, 3+ pitting edema",
        correctAnswer: "Acute Decompensated Heart Failure",
        options: [
          "Acute Decompensated Heart Failure",
          "Acute Coronary Syndrome",
          "Nephrotic Syndrome",
          "Cirrhosis with Ascites",
        ],
        explanation: "Markedly elevated BNP with normal troponin indicates heart failure without acute myocardial injury. Volume overload signs (crackles, JVD, edema) and dilutional hyponatremia are consistent with decompensated CHF. Renal dysfunction is from cardiorenal syndrome.",
      },
      {
        id: "cardiac-14",
        patientInfo: "45-year-old male with palpitations and near-syncope",
        labResults: [
          { test: "Potassium", value: "2.4 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Magnesium", value: "1.2 mg/dL", normal: "1.7-2.2 mg/dL", abnormal: true },
          { test: "Troponin I", value: "0.01 ng/mL", normal: "<0.04 ng/mL", abnormal: false },
          { test: "TSH", value: "0.8 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: false },
        ],
        additionalInfo: "Taking hydrochlorothiazide, ECG shows prolonged QT and frequent PVCs",
        correctAnswer: "Hypokalemia-Induced Arrhythmia",
        options: [
          "Hypokalemia-Induced Arrhythmia",
          "Hyperthyroidism",
          "Acute Coronary Syndrome",
          "Brugada Syndrome",
        ],
        explanation: "Severe hypokalemia and hypomagnesemia from thiazide diuretic causing QT prolongation and ventricular ectopy. Both electrolytes must be repleted; magnesium helps stabilize cardiac membranes and facilitates potassium repletion. Risk of torsades de pointes.",
      },
      {
        id: "cardiac-15",
        patientInfo: "65-year-old female with exertional chest tightness and dyspnea",
        labResults: [
          { test: "Troponin I", value: "0.15 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "BNP", value: "450 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "Hemoglobin", value: "8.2 g/dL", normal: "12-16 g/dL", abnormal: true },
          { test: "Creatinine", value: "2.4 mg/dL", normal: "0.5-1.1 mg/dL", abnormal: true },
        ],
        additionalInfo: "History of CKD, troponin stable over 6 hours, no ST changes on ECG",
        correctAnswer: "Type 2 MI (Demand Ischemia)",
        options: [
          "Type 2 MI (Demand Ischemia)",
          "Type 1 MI (Plaque Rupture)",
          "Chronic Troponin Elevation from CKD",
          "Takotsubo Cardiomyopathy",
        ],
        explanation: "Elevated but stable troponin with severe anemia indicates demand ischemia (Type 2 MI) rather than acute plaque rupture. The myocardium is stressed by reduced oxygen delivery from anemia. Treatment focuses on transfusion and addressing the underlying cause, not urgent catheterization.",
      },
      {
        id: "cardiac-16",
        patientInfo: "28-year-old male athlete who collapsed during marathon",
        labResults: [
          { test: "Troponin I", value: "2.8 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "CK-MB", value: "85 ng/mL", normal: "<5 ng/mL", abnormal: true },
          { test: "CK total", value: "15,000 U/L", normal: "30-200 U/L", abnormal: true },
          { test: "Potassium", value: "5.8 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
        ],
        additionalInfo: "Core temperature 104°F, dark urine, muscle tenderness, no ST elevation on ECG",
        correctAnswer: "Exertional Rhabdomyolysis with Cardiac Stress",
        options: [
          "Exertional Rhabdomyolysis with Cardiac Stress",
          "Acute STEMI",
          "Heat Stroke without Muscle Injury",
          "Hypertrophic Cardiomyopathy",
        ],
        explanation: "Massively elevated CK with troponin elevation in setting of extreme exertion and hyperthermia indicates rhabdomyolysis. Troponin release can occur from cardiac stress/strain without coronary occlusion. Dark urine suggests myoglobinuria. Treatment: aggressive IV fluids, monitor for AKI and hyperkalemia.",
      },
      {
        id: "cardiac-17",
        patientInfo: "55-year-old female with chest pain worse when lying down, better leaning forward",
        labResults: [
          { test: "Troponin I", value: "0.52 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "ESR", value: "65 mm/hr", normal: "<20 mm/hr", abnormal: true },
          { test: "CRP", value: "8.5 mg/dL", normal: "<0.5 mg/dL", abnormal: true },
          { test: "WBC", value: "11,500/μL", normal: "4,000-11,000/μL", abnormal: true },
        ],
        additionalInfo: "Recent flu-like illness, diffuse ST elevation with PR depression on ECG, pericardial friction rub",
        correctAnswer: "Acute Pericarditis with Myocardial Involvement",
        options: [
          "Acute Pericarditis with Myocardial Involvement",
          "Acute STEMI",
          "Costochondritis",
          "Pneumonia with Pleurisy",
        ],
        explanation: "Positional chest pain, friction rub, diffuse ST elevation with PR depression, and elevated inflammatory markers after viral illness indicate pericarditis. Troponin elevation suggests myopericarditis (myocardial involvement). STEMI has regional ST changes and reciprocal depression.",
      },
      {
        id: "cardiac-18",
        patientInfo: "68-year-old male with syncope and bradycardia",
        labResults: [
          { test: "Digoxin level", value: "3.8 ng/mL", normal: "0.8-2.0 ng/mL", abnormal: true },
          { test: "Potassium", value: "5.6 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Creatinine", value: "2.8 mg/dL", normal: "0.7-1.3 mg/dL", abnormal: true },
          { test: "BUN", value: "45 mg/dL", normal: "7-20 mg/dL", abnormal: true },
        ],
        additionalInfo: "On digoxin for atrial fibrillation, recent dehydration from gastroenteritis, visual changes",
        correctAnswer: "Digoxin Toxicity",
        options: [
          "Digoxin Toxicity",
          "Sick Sinus Syndrome",
          "Complete Heart Block",
          "Hyperkalemia-Induced Bradycardia",
        ],
        explanation: "Elevated digoxin level with bradycardia, visual changes (yellow-green halos), and GI symptoms indicate toxicity. Acute kidney injury from dehydration reduced digoxin clearance. Hyperkalemia worsens digoxin toxicity. Treatment: hold digoxin, correct electrolytes, consider digoxin-specific antibody fragments.",
      },
      {
        id: "cardiac-19",
        patientInfo: "50-year-old female with chest pain and recent emotional stress",
        labResults: [
          { test: "Troponin I", value: "1.2 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "BNP", value: "680 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "CK-MB", value: "12 ng/mL", normal: "<5 ng/mL", abnormal: true },
          { test: "Catecholamines", value: "Markedly elevated", normal: "Normal", abnormal: true },
        ],
        additionalInfo: "Death of spouse 2 days ago, ECG shows T-wave inversions V2-V6, echo shows apical ballooning with EF 35%",
        correctAnswer: "Takotsubo (Stress) Cardiomyopathy",
        options: [
          "Takotsubo (Stress) Cardiomyopathy",
          "Anterior STEMI",
          "Myocarditis",
          "Hypertensive Emergency",
        ],
        explanation: "Classic Takotsubo: post-menopausal female with recent severe emotional stress, elevated troponins out of proportion to wall motion abnormality, apical ballooning on echo. Caused by catecholamine surge. Coronary arteries are typically normal. Usually reversible within weeks.",
      },
      {
        id: "cardiac-20",
        patientInfo: "62-year-old male with diabetes presenting with fatigue and nausea",
        labResults: [
          { test: "Troponin I", value: "4.5 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "BNP", value: "520 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "Glucose", value: "285 mg/dL", normal: "70-100 mg/dL", abnormal: true },
          { test: "HbA1c", value: "9.2%", normal: "<5.7%", abnormal: true },
        ],
        additionalInfo: "No chest pain, mild dyspnea, ECG shows ST depression in V4-V6, diaphoretic",
        correctAnswer: "Silent MI (NSTEMI in Diabetic)",
        options: [
          "Silent MI (NSTEMI in Diabetic)",
          "Diabetic Ketoacidosis",
          "Hypoglycemia",
          "Anxiety Attack",
        ],
        explanation: "Diabetic patients often have 'silent' MIs without classic chest pain due to autonomic neuropathy. Atypical symptoms (fatigue, nausea, dyspnea, diaphoresis) with markedly elevated troponin and ST changes confirm NSTEMI. High index of suspicion needed in diabetics with vague symptoms.",
      },
      {
        id: "cardiac-21",
        patientInfo: "75-year-old male with new atrial fibrillation and hypotension",
        labResults: [
          { test: "TSH", value: "0.01 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: true },
          { test: "Free T4", value: "5.8 ng/dL", normal: "0.8-1.8 ng/dL", abnormal: true },
          { test: "Free T3", value: "12.5 pg/mL", normal: "2.3-4.2 pg/mL", abnormal: true },
          { test: "Troponin I", value: "0.08 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
        ],
        additionalInfo: "Unintentional weight loss, tremor, warm skin, taking amiodarone for 2 years",
        correctAnswer: "Amiodarone-Induced Thyrotoxicosis",
        options: [
          "Amiodarone-Induced Thyrotoxicosis",
          "Graves Disease",
          "Thyroid Storm",
          "Sepsis-Induced Atrial Fibrillation",
        ],
        explanation: "Amiodarone contains iodine and can cause thyrotoxicosis (Type 1: increased synthesis in underlying thyroid disease, Type 2: destructive thyroiditis). New AF with hyperthyroid labs in patient on amiodarone is classic. May require stopping amiodarone and treatment with steroids or thionamides depending on type.",
      },
      {
        id: "cardiac-22",
        patientInfo: "38-year-old female, 6 weeks post-partum, sudden onset dyspnoea, pleuritic chest pain, and haemodynamic collapse",
        labResults: [
          { test: "D-Dimer", value: "8,200 μg/L", normal: "<500 μg/L", abnormal: true },
          { test: "Troponin I", value: "0.65 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "NT-proBNP", value: "4,800 pg/mL", normal: "<125 pg/mL", abnormal: true },
          { test: "PaO2 (ABG)", value: "55 mmHg", normal: "80-100 mmHg", abnormal: true },
          { test: "pCO2", value: "28 mmHg", normal: "35-45 mmHg", abnormal: true },
          { test: "pH", value: "7.48", normal: "7.35-7.45", abnormal: true },
        ],
        additionalInfo: "ECG: S1Q3T3 pattern and new right bundle branch block. CTPA confirmed bilateral central saddle pulmonary embolism with right ventricular dilatation.",
        correctAnswer: "Massive Pulmonary Embolism",
        options: [
          "Massive Pulmonary Embolism",
          "Acute MI",
          "Acute Decompensated Heart Failure",
          "Aortic Dissection",
        ],
        explanation: "Massive PE (haemodynamic compromise, SBP <90): elevated D-dimer (sensitive but non-specific — positive in any thrombosis, inflammation, pregnancy, surgery, malignancy). Troponin elevation = RV myocardial injury from strain. NT-proBNP elevation = RV wall stress. ABG: hypoxaemia + hypocapnia (hyperventilation) → respiratory alkalosis. S1Q3T3 classic but only in ~20%. Risk stratification: PESI score + echo (RV dilatation). Massive PE requires systemic thrombolysis (alteplase) or catheter-directed therapy/surgical embolectomy.",
      },
      {
        id: "cardiac-23",
        patientInfo: "24-year-old male with chest pain and dyspnoea 2 weeks after a viral upper respiratory illness",
        labResults: [
          { test: "Troponin I", value: "2.8 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "CK-MB", value: "18 ng/mL", normal: "<5 ng/mL", abnormal: true },
          { test: "BNP", value: "320 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "CRP", value: "45 mg/L", normal: "<5 mg/L", abnormal: true },
          { test: "ESR", value: "68 mm/hr", normal: "<20 mm/hr", abnormal: true },
          { test: "Coxsackievirus B IgM", value: "Positive", normal: "Negative", abnormal: true },
        ],
        additionalInfo: "ECG: diffuse saddle-shaped ST elevation in all leads without reciprocal changes. Echo: LV ejection fraction 40%, diffuse hypokinesis. Coronary angiogram: no obstructive disease.",
        correctAnswer: "Viral Myocarditis",
        options: [
          "Viral Myocarditis",
          "ST-Elevation MI (STEMI)",
          "Acute Pericarditis",
          "Takotsubo Cardiomyopathy",
        ],
        explanation: "Myocarditis: diffuse myocardial inflammation, typically post-viral (Coxsackievirus B, adenovirus, parvovirus B19, SARS-CoV-2). Elevated troponin + impaired global LV function + inflammatory markers + preceding viral illness. Distinguishing from STEMI: diffuse (not territorial) ST elevation, younger patient, no obstructive coronaries. Distinguishing from pericarditis: LV dysfunction and higher troponin suggest myocarditis. Cardiac MRI with gadolinium (LGE pattern) is diagnostic gold standard. Rest and avoid exercise; risk of sudden death in active phase.",
      },
      {
        id: "cardiac-24",
        patientInfo: "68-year-old woman with chest pain and dyspnoea occurring immediately after receiving news of her husband's sudden death",
        labResults: [
          { test: "Troponin I", value: "1.4 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "NT-proBNP", value: "1,850 pg/mL", normal: "<125 pg/mL", abnormal: true },
          { test: "CK-MB", value: "12 ng/mL", normal: "<5 ng/mL", abnormal: true },
          { test: "Cortisol", value: "920 nmol/L", normal: "<600 nmol/L", abnormal: true },
          { test: "Urine Catecholamines", value: "Mildly elevated", normal: "Normal", abnormal: true },
        ],
        additionalInfo: "ECG: anterior ST elevation, then widespread T-wave inversion. Angiogram: no obstructive coronary disease. Echo: apical ballooning with preserved basal function — classic octopus-trap appearance.",
        correctAnswer: "Takotsubo (Stress) Cardiomyopathy",
        options: [
          "Takotsubo (Stress) Cardiomyopathy",
          "Anterior STEMI",
          "Phaeochromocytoma",
          "Hypertensive Heart Disease",
        ],
        explanation: "Takotsubo (broken heart syndrome): catecholamine surge → apical ballooning with preserved basal function mimicking STEMI. Predominantly post-menopausal women after emotional or physical stress. Troponin and BNP elevated but typically lower than equivalent-territory STEMI. Named after Japanese octopus trap (tako-tsubo). Normal or near-normal coronary arteries on angiography. Usually reversible within 4-8 weeks. Complications: cardiogenic shock, arrhythmia, LV thrombus. Management: supportive; ACE inhibitors and beta-blockers during recovery.",
      },
      {
        id: "cardiac-25",
        patientInfo: "42-year-old male with premature CAD, father died of MI aged 48, Achilles tendon xanthomata on examination",
        labResults: [
          { test: "Total Cholesterol", value: "9.8 mmol/L (380 mg/dL)", normal: "<5.0 mmol/L", abnormal: true },
          { test: "LDL Cholesterol", value: "7.6 mmol/L (295 mg/dL)", normal: "<3.0 mmol/L", abnormal: true },
          { test: "HDL Cholesterol", value: "1.1 mmol/L", normal: ">1.0 mmol/L", abnormal: false },
          { test: "Triglycerides", value: "1.8 mmol/L", normal: "<1.7 mmol/L", abnormal: true },
          { test: "Lp(a)", value: "180 nmol/L", normal: "<75 nmol/L", abnormal: true },
          { test: "Fasting Glucose", value: "5.4 mmol/L", normal: "<6.1 mmol/L", abnormal: false },
        ],
        additionalInfo: "Corneal arcus, xanthelasma, and Achilles tendon xanthomata. Dutch Lipid Clinic Network Score: 10 (definite FH). PCSK9 gene mutation identified on testing.",
        correctAnswer: "Familial Hypercholesterolaemia",
        options: [
          "Familial Hypercholesterolaemia",
          "Secondary Hyperlipidaemia (hypothyroidism)",
          "Familial Combined Hyperlipidaemia",
          "Polygenic Hypercholesterolaemia",
        ],
        explanation: "Familial hypercholesterolaemia (FH): LDL receptor mutations (AD) causing severely elevated LDL from birth. Markedly elevated LDL + family history of premature CVD + physical signs (tendon xanthomata = pathognomonic; corneal arcus < 45 years). Elevated Lp(a) is an additional independent risk factor. Cascade family screening is essential. Treatment: high-intensity statin + ezetimibe; PCSK9 inhibitors (evolocumab, alirocumab) if LDL remains >5 mmol/L despite maximum therapy. Secondary causes (hypothyroidism, nephrotic syndrome, cholestasis) must be excluded.",
      },
      {
        id: "cardiac-26",
        patientInfo: "68-year-old male with GOLD Stage 4 COPD, worsening bilateral leg oedema, raised JVP, and declining exercise tolerance",
        labResults: [
          { test: "NT-proBNP", value: "3,800 pg/mL", normal: "<125 pg/mL", abnormal: true },
          { test: "BNP", value: "620 pg/mL", normal: "<100 pg/mL", abnormal: true },
          { test: "Troponin I", value: "0.06 ng/mL", normal: "<0.04 ng/mL", abnormal: true },
          { test: "Haemoglobin", value: "19.2 g/dL", normal: "13.5-17.5 g/dL", abnormal: true },
          { test: "pCO2 (ABG)", value: "58 mmHg", normal: "35-45 mmHg", abnormal: true },
          { test: "HCO3 (ABG)", value: "32 mEq/L", normal: "22-28 mEq/L", abnormal: true },
        ],
        additionalInfo: "Echo: RV dilatation, RVSP 65 mmHg, tricuspid regurgitation. Oxygen saturation 84% on air. No LV dysfunction. JVP raised with prominent v-wave.",
        correctAnswer: "Cor Pulmonale (Right Heart Failure from COPD)",
        options: [
          "Cor Pulmonale (Right Heart Failure from COPD)",
          "Left Heart Failure",
          "Massive Pulmonary Embolism",
          "Cardiac Tamponade",
        ],
        explanation: "Cor pulmonale: RV failure secondary to pulmonary hypertension from chronic lung disease. Elevated BNP = RV wall stress (not LV). Compensated respiratory acidosis: chronic CO2 retention (raised pCO2) with renal HCO3 retention (raised HCO3) — pH near normal distinguishes chronic from acute. Secondary polycythaemia (EPO response to chronic hypoxia) = elevated Hb. Echo shows RV, not LV, dysfunction. LTOT (>15 hr/day) is the only treatment proven to improve survival in COPD with hypoxaemia (PaO2 <7.3 kPa).",
      },
    ],
    endocrine: [
      {
        id: "endo-1",
        patientInfo: "38-year-old female with weight gain, fatigue, and cold intolerance",
        labResults: [
          { test: "TSH", value: "18.5 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: true },
          { test: "Free T4", value: "0.6 ng/dL", normal: "0.8-1.8 ng/dL", abnormal: true },
          { test: "Free T3", value: "1.8 pg/mL", normal: "2.3-4.2 pg/mL", abnormal: true },
        ],
        additionalInfo: "Dry skin, bradycardia, delayed deep tendon reflexes",
        correctAnswer: "Primary Hypothyroidism",
        options: [
          "Primary Hypothyroidism",
          "Secondary Hypothyroidism",
          "Subclinical Hypothyroidism",
          "Euthyroid Sick Syndrome",
        ],
        explanation: "Elevated TSH with low Free T4 and T3 indicates primary hypothyroidism (thyroid gland failure). The pituitary is appropriately increasing TSH to try to stimulate the failing thyroid. Secondary hypothyroidism would have low TSH. Requires levothyroxine replacement.",
      },
      {
        id: "endo-2",
        patientInfo: "28-year-old female with palpitations, weight loss, and tremor",
        labResults: [
          { test: "TSH", value: "0.01 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: true },
          { test: "Free T4", value: "3.2 ng/dL", normal: "0.8-1.8 ng/dL", abnormal: true },
          { test: "Free T3", value: "7.8 pg/mL", normal: "2.3-4.2 pg/mL", abnormal: true },
          { test: "TSI", value: "Positive", normal: "Negative", abnormal: true },
        ],
        additionalInfo: "Exophthalmos (bulging eyes), goiter present",
        correctAnswer: "Graves' Disease",
        options: [
          "Graves' Disease",
          "Toxic Multinodular Goiter",
          "Subacute Thyroiditis",
          "Thyroid Cancer",
        ],
        explanation: "Suppressed TSH with elevated T4 and T3 indicates hyperthyroidism. Positive TSI (thyroid-stimulating immunoglobulin) antibodies confirm Graves' disease, an autoimmune condition. Exophthalmos is pathognomonic for Graves'. Treatment includes methimazole, beta-blockers, and possibly radioactive iodine.",
      },
      {
        id: "endo-3",
        patientInfo: "52-year-old male with polyuria, polydipsia, and blurred vision",
        labResults: [
          { test: "Fasting Glucose", value: "245 mg/dL", normal: "70-100 mg/dL", abnormal: true },
          { test: "HbA1c", value: "10.2%", normal: "<5.7%", abnormal: true },
          { test: "C-peptide", value: "2.8 ng/mL", normal: "0.8-3.1 ng/mL", abnormal: false },
        ],
        additionalInfo: "BMI 34, family history of diabetes",
        correctAnswer: "Type 2 Diabetes Mellitus",
        options: [
          "Type 2 Diabetes Mellitus",
          "Type 1 Diabetes Mellitus",
          "Prediabetes",
          "Diabetes Insipidus",
        ],
        explanation: "Markedly elevated fasting glucose and HbA1c (>6.5% diagnostic for diabetes) with normal C-peptide indicates preserved insulin production, consistent with Type 2 diabetes. Obesity and family history are risk factors. HbA1c of 10.2% indicates poor glycemic control over past 3 months. Requires lifestyle modification and likely metformin.",
      },
      {
        id: "endo-4",
        patientInfo: "42-year-old female with weakness and darkening of skin",
        labResults: [
          { test: "Cortisol (AM)", value: "2.5 μg/dL", normal: "7-25 μg/dL", abnormal: true },
          { test: "ACTH", value: "185 pg/mL", normal: "10-60 pg/mL", abnormal: true },
          { test: "Sodium", value: "128 mEq/L", normal: "135-145 mEq/L", abnormal: true },
          { test: "Potassium", value: "5.8 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
        ],
        additionalInfo: "Hypotension, hyperpigmentation in skin creases",
        correctAnswer: "Primary Adrenal Insufficiency (Addison's)",
        options: [
          "Primary Adrenal Insufficiency (Addison's)",
          "Secondary Adrenal Insufficiency",
          "Cushing's Syndrome",
          "Conn's Syndrome",
        ],
        explanation: "Low cortisol with elevated ACTH indicates primary adrenal insufficiency (adrenal glands failing). High ACTH causes hyperpigmentation. Hyponatremia and hyperkalemia from mineralocorticoid deficiency. Secondary adrenal insufficiency would have low ACTH. Life-threatening condition requiring hydrocortisone and fludrocortisone replacement.",
      },
      {
        id: "endo-5",
        patientInfo: "60-year-old male with extreme thirst and urinating 8 liters per day",
        labResults: [
          { test: "Serum Osmolality", value: "310 mOsm/kg", normal: "275-295 mOsm/kg", abnormal: true },
          { test: "Urine Osmolality", value: "95 mOsm/kg", normal: "300-900 mOsm/kg", abnormal: true },
          { test: "Sodium", value: "152 mEq/L", normal: "135-145 mEq/L", abnormal: true },
          { test: "Glucose", value: "92 mg/dL", normal: "70-100 mg/dL", abnormal: false },
        ],
        additionalInfo: "Recent brain surgery for pituitary tumor, unable to concentrate urine",
        correctAnswer: "Central Diabetes Insipidus",
        options: [
          "Central Diabetes Insipidus",
          "Nephrogenic Diabetes Insipidus",
          "Diabetes Mellitus",
          "Psychogenic Polydipsia",
        ],
        explanation: "High serum osmolality with inappropriately dilute urine (low urine osmolality) indicates diabetes insipidus. Recent pituitary surgery suggests central DI from ADH deficiency. Normal glucose rules out diabetes mellitus. Treatment is desmopressin (DDAVP).",
      },
      {
        id: "endo-6",
        patientInfo: "25-year-old female with irregular periods and galactorrhea",
        labResults: [
          { test: "Prolactin", value: "185 ng/mL", normal: "4-23 ng/mL", abnormal: true },
          { test: "TSH", value: "2.5 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: false },
          { test: "LH", value: "Low", normal: "Variable", abnormal: true },
          { test: "FSH", value: "Low", normal: "Variable", abnormal: true },
        ],
        additionalInfo: "Headaches, MRI shows 8mm pituitary mass",
        correctAnswer: "Prolactinoma (Pituitary Adenoma)",
        options: [
          "Prolactinoma (Pituitary Adenoma)",
          "Hypothyroidism",
          "Medication-Induced Hyperprolactinemia",
          "Pregnancy",
        ],
        explanation: "Very elevated prolactin (>100) with pituitary mass strongly suggests prolactinoma. High prolactin inhibits GnRH, causing low LH/FSH and amenorrhea. Normal TSH rules out hypothyroidism as cause. Treatment is dopamine agonist (cabergoline) which shrinks tumor.",
      },
      {
        id: "endo-7",
        patientInfo: "45-year-old male with muscle weakness and hypertension",
        labResults: [
          { test: "Potassium", value: "2.9 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Sodium", value: "147 mEq/L", normal: "135-145 mEq/L", abnormal: true },
          { test: "Aldosterone", value: "35 ng/dL", normal: "3-16 ng/dL", abnormal: true },
          { test: "Renin", value: "Suppressed", normal: "Normal", abnormal: true },
        ],
        additionalInfo: "Resistant hypertension on 3 medications, CT shows adrenal nodule",
        correctAnswer: "Primary Hyperaldosteronism (Conn's Syndrome)",
        options: [
          "Primary Hyperaldosteronism (Conn's Syndrome)",
          "Secondary Hyperaldosteronism",
          "Cushing's Syndrome",
          "Pheochromocytoma",
        ],
        explanation: "Elevated aldosterone with suppressed renin (high aldosterone:renin ratio) indicates primary hyperaldosteronism. Aldosterone causes sodium retention (hypertension) and potassium wasting (hypokalemia). Adrenal nodule suggests aldosterone-producing adenoma (Conn's syndrome).",
      },
      {
        id: "endo-8",
        patientInfo: "55-year-old female with unexplained weight loss and diarrhea",
        labResults: [
          { test: "TSH", value: "0.05 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: true },
          { test: "Free T4", value: "2.8 ng/dL", normal: "0.8-1.8 ng/dL", abnormal: true },
          { test: "Free T3", value: "6.5 pg/mL", normal: "2.3-4.2 pg/mL", abnormal: true },
          { test: "Thyroid uptake scan", value: "Low uptake", normal: "Normal uptake", abnormal: true },
        ],
        additionalInfo: "Tender thyroid, recent upper respiratory infection, elevated ESR",
        correctAnswer: "Subacute Thyroiditis (De Quervain's)",
        options: [
          "Subacute Thyroiditis (De Quervain's)",
          "Graves' Disease",
          "Toxic Multinodular Goiter",
          "Thyroid Storm",
        ],
        explanation: "Hyperthyroidism (low TSH, high T4/T3) with LOW uptake on thyroid scan indicates thyroiditis (hormone leaking from damaged gland, not increased production). Tender thyroid after viral illness and high ESR confirms subacute thyroiditis. Unlike Graves', treatment is supportive, not antithyroid drugs.",
      },
      {
        id: "endo-9",
        patientInfo: "50-year-old female with central obesity and easy bruising",
        labResults: [
          { test: "24-hour Urine Cortisol", value: "385 μg/day", normal: "<100 μg/day", abnormal: true },
          { test: "Late-night Salivary Cortisol", value: "Elevated", normal: "Low at night", abnormal: true },
          { test: "ACTH", value: "75 pg/mL", normal: "10-60 pg/mL", abnormal: true },
          { test: "Dexamethasone Suppression Test", value: "No suppression", normal: "Suppression", abnormal: true },
        ],
        additionalInfo: "Moon facies, buffalo hump, purple striae, hypertension, MRI shows pituitary microadenoma",
        correctAnswer: "Cushing's Disease (Pituitary ACTH Adenoma)",
        options: [
          "Cushing's Disease (Pituitary ACTH Adenoma)",
          "Adrenal Cushing's Syndrome",
          "Ectopic ACTH Syndrome",
          "Exogenous Steroid Use",
        ],
        explanation: "Elevated cortisol that doesn't suppress with dexamethasone, elevated ACTH, and pituitary adenoma confirms Cushing's disease. Adrenal tumors have suppressed ACTH. Ectopic ACTH would have very high ACTH and usually lung mass.",
      },
      {
        id: "endo-10",
        patientInfo: "22-year-old male with tall stature and delayed puberty",
        labResults: [
          { test: "Testosterone", value: "Low", normal: "Normal for age", abnormal: true },
          { test: "LH", value: "Elevated", normal: "Normal", abnormal: true },
          { test: "FSH", value: "Elevated", normal: "Normal", abnormal: true },
          { test: "Karyotype", value: "47,XXY", normal: "46,XY", abnormal: true },
        ],
        additionalInfo: "Gynecomastia, small firm testes, learning difficulties",
        correctAnswer: "Klinefelter Syndrome",
        options: [
          "Klinefelter Syndrome",
          "Kallmann Syndrome",
          "Constitutional Delay of Puberty",
          "Pituitary Tumor",
        ],
        explanation: "Low testosterone with HIGH gonadotropins (LH, FSH) indicates primary hypogonadism. 47,XXY karyotype confirms Klinefelter syndrome. Small firm testes, gynecomastia, and tall stature are classic features. Requires testosterone replacement.",
      },
      {
        id: "endo-11",
        patientInfo: "65-year-old female with confusion and lethargy",
        labResults: [
          { test: "Sodium", value: "118 mEq/L", normal: "135-145 mEq/L", abnormal: true },
          { test: "Serum Osmolality", value: "250 mOsm/kg", normal: "275-295 mOsm/kg", abnormal: true },
          { test: "Urine Osmolality", value: "450 mOsm/kg", normal: "50-100 mOsm/kg (dilute with low serum)", abnormal: true },
          { test: "Urine Sodium", value: ">40 mEq/L", normal: "Variable", abnormal: true },
        ],
        additionalInfo: "Recent diagnosis of small cell lung cancer, euvolemic on exam",
        correctAnswer: "SIADH (Syndrome of Inappropriate ADH)",
        options: [
          "SIADH (Syndrome of Inappropriate ADH)",
          "Psychogenic Polydipsia",
          "Adrenal Insufficiency",
          "Heart Failure",
        ],
        explanation: "Hyponatremia with low serum osmolality but inappropriately concentrated urine (high urine osmolality) indicates SIADH. Small cell lung cancer is a classic cause (ectopic ADH production). Patient is euvolemic, unlike heart failure.",
      },
      {
        id: "endo-12",
        patientInfo: "35-year-old female with anxiety and tremor",
        labResults: [
          { test: "Calcium", value: "7.2 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Phosphorus", value: "5.8 mg/dL", normal: "2.5-4.5 mg/dL", abnormal: true },
          { test: "PTH", value: "8 pg/mL", normal: "15-65 pg/mL", abnormal: true },
          { test: "Magnesium", value: "1.8 mg/dL", normal: "1.7-2.2 mg/dL", abnormal: false },
        ],
        additionalInfo: "Recent thyroidectomy, perioral numbness, positive Chvostek sign",
        correctAnswer: "Hypoparathyroidism (Post-Surgical)",
        options: [
          "Hypoparathyroidism (Post-Surgical)",
          "Vitamin D Deficiency",
          "Pseudohypoparathyroidism",
          "Chronic Kidney Disease",
        ],
        explanation: "Low calcium and high phosphorus with LOW PTH (inappropriately low) after thyroid surgery indicates surgical hypoparathyroidism from parathyroid damage. Chvostek sign (facial twitching) indicates hypocalcemic tetany. Requires calcium and vitamin D replacement.",
      },
      {
        id: "endo-13",
        patientInfo: "16-year-old female with nausea, vomiting, and abdominal pain",
        labResults: [
          { test: "Glucose", value: "485 mg/dL", normal: "70-100 mg/dL", abnormal: true },
          { test: "pH", value: "7.18", normal: "7.35-7.45", abnormal: true },
          { test: "HCO3", value: "10 mEq/L", normal: "22-28 mEq/L", abnormal: true },
          { test: "Anion Gap", value: "28", normal: "8-12", abnormal: true },
          { test: "Beta-hydroxybutyrate", value: "6.2 mmol/L", normal: "<0.6 mmol/L", abnormal: true },
        ],
        additionalInfo: "Recent weight loss, polyuria, Kussmaul respirations, fruity breath odor",
        correctAnswer: "Diabetic Ketoacidosis (DKA)",
        options: [
          "Diabetic Ketoacidosis (DKA)",
          "Hyperosmolar Hyperglycemic State",
          "Lactic Acidosis",
          "Alcoholic Ketoacidosis",
        ],
        explanation: "Hyperglycemia with metabolic acidosis (low pH, low HCO3), elevated anion gap, and high ketones confirms DKA. Kussmaul respirations are compensatory deep breathing. Common presentation for new-onset Type 1 diabetes. Treatment: IV fluids, insulin, potassium replacement.",
      },
      {
        id: "endo-14",
        patientInfo: "48-year-old male with episodic headaches, sweating, and palpitations",
        labResults: [
          { test: "24-hour Urine Metanephrines", value: "2450 μg", normal: "<400 μg", abnormal: true },
          { test: "Plasma Metanephrines", value: "Elevated", normal: "Normal", abnormal: true },
          { test: "Glucose", value: "165 mg/dL", normal: "70-100 mg/dL", abnormal: true },
          { test: "Calcium", value: "11.2 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
        ],
        additionalInfo: "Paroxysmal hypertension (BP spikes to 220/120), CT shows 4cm adrenal mass",
        correctAnswer: "Pheochromocytoma",
        options: [
          "Pheochromocytoma",
          "Primary Hyperaldosteronism",
          "Essential Hypertension",
          "Carcinoid Syndrome",
        ],
        explanation: "Classic triad of headaches, sweating, and palpitations with paroxysmal hypertension suggests pheochromocytoma. Markedly elevated urinary metanephrines confirm catecholamine excess. Hyperglycemia from catecholamine-induced gluconeogenesis. Rule out MEN2 (hypercalcemia may indicate associated hyperparathyroidism).",
      },
      {
        id: "endo-15",
        patientInfo: "32-year-old female with fatigue and inability to lose weight despite exercise",
        labResults: [
          { test: "TSH", value: "8.5 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: true },
          { test: "Free T4", value: "0.9 ng/dL", normal: "0.8-1.8 ng/dL", abnormal: false },
          { test: "Anti-TPO Antibodies", value: "485 IU/mL", normal: "<35 IU/mL", abnormal: true },
          { test: "Anti-Thyroglobulin Ab", value: "Positive", normal: "Negative", abnormal: true },
        ],
        additionalInfo: "Family history of thyroid disease, slightly enlarged thyroid",
        correctAnswer: "Hashimoto's Thyroiditis (Subclinical)",
        options: [
          "Hashimoto's Thyroiditis (Subclinical)",
          "Graves' Disease",
          "Subacute Thyroiditis",
          "Thyroid Cancer",
        ],
        explanation: "Elevated TSH with normal Free T4 indicates subclinical hypothyroidism. Highly positive anti-TPO antibodies confirm autoimmune Hashimoto's thyroiditis. Most common cause of hypothyroidism in developed countries. May progress to overt hypothyroidism; many endocrinologists treat if TSH >10 or symptoms present.",
      },
      {
        id: "endo-16",
        patientInfo: "58-year-old male with bone pain and kidney stones",
        labResults: [
          { test: "Calcium", value: "12.8 mg/dL", normal: "8.5-10.5 mg/dL", abnormal: true },
          { test: "Phosphorus", value: "2.1 mg/dL", normal: "2.5-4.5 mg/dL", abnormal: true },
          { test: "PTH", value: "145 pg/mL", normal: "15-65 pg/mL", abnormal: true },
          { test: "Vitamin D (25-OH)", value: "32 ng/mL", normal: "30-100 ng/mL", abnormal: false },
          { test: "24-hr Urine Calcium", value: "420 mg", normal: "<300 mg", abnormal: true },
        ],
        additionalInfo: "History of recurrent nephrolithiasis, osteoporosis on DEXA",
        correctAnswer: "Primary Hyperparathyroidism",
        options: [
          "Primary Hyperparathyroidism",
          "Hypercalcemia of Malignancy",
          "Vitamin D Toxicity",
          "Familial Hypocalciuric Hypercalcemia",
        ],
        explanation: "Elevated calcium with elevated PTH (inappropriately high) indicates primary hyperparathyroidism. Low phosphorus due to PTH-induced renal phosphate wasting. 'Stones, bones, groans, and moans' describes classic symptoms. Parathyroid adenoma is most common cause; treatment is surgical parathyroidectomy.",
      },
      {
        id: "endo-17",
        patientInfo: "72-year-old male with altered mental status and severe dehydration",
        labResults: [
          { test: "Glucose", value: "892 mg/dL", normal: "70-100 mg/dL", abnormal: true },
          { test: "Serum Osmolality", value: "358 mOsm/kg", normal: "275-295 mOsm/kg", abnormal: true },
          { test: "pH", value: "7.32", normal: "7.35-7.45", abnormal: true },
          { test: "HCO3", value: "20 mEq/L", normal: "22-28 mEq/L", abnormal: true },
          { test: "Ketones", value: "Trace", normal: "Negative", abnormal: false },
        ],
        additionalInfo: "Nursing home resident with known Type 2 diabetes, recent pneumonia",
        correctAnswer: "Hyperosmolar Hyperglycemic State (HHS)",
        options: [
          "Hyperosmolar Hyperglycemic State (HHS)",
          "Diabetic Ketoacidosis",
          "Sepsis-induced Hyperglycemia",
          "Steroid-induced Diabetes",
        ],
        explanation: "Extreme hyperglycemia (>600) with very high serum osmolality (>320), minimal ketosis, and relatively preserved pH distinguishes HHS from DKA. Common in elderly Type 2 diabetics with precipitating illness. Higher mortality than DKA. Treatment: aggressive IV fluids, then insulin.",
      },
      {
        id: "endo-18",
        patientInfo: "28-year-old female with rapid weight loss after pregnancy",
        labResults: [
          { test: "TSH", value: "0.02 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: true },
          { test: "Free T4", value: "2.4 ng/dL", normal: "0.8-1.8 ng/dL", abnormal: true },
          { test: "Free T3", value: "5.2 pg/mL", normal: "2.3-4.2 pg/mL", abnormal: true },
          { test: "TSI", value: "Negative", normal: "Negative", abnormal: false },
          { test: "Anti-TPO Antibodies", value: "220 IU/mL", normal: "<35 IU/mL", abnormal: true },
        ],
        additionalInfo: "2 months postpartum, painless goiter, planning to breastfeed",
        correctAnswer: "Postpartum Thyroiditis (Thyrotoxic Phase)",
        options: [
          "Postpartum Thyroiditis (Thyrotoxic Phase)",
          "Graves' Disease",
          "Toxic Adenoma",
          "Factitious Hyperthyroidism",
        ],
        explanation: "Postpartum hyperthyroidism with negative TSI and positive anti-TPO indicates postpartum thyroiditis, not Graves'. This autoimmune destructive thyroiditis releases preformed hormone. Typically self-limited; many patients later develop hypothyroid phase. Beta-blockers for symptoms; antithyroid drugs not effective.",
      },
      {
        id: "endo-19",
        patientInfo: "40-year-old male with decreased libido and erectile dysfunction",
        labResults: [
          { test: "Total Testosterone", value: "180 ng/dL", normal: "300-1000 ng/dL", abnormal: true },
          { test: "LH", value: "2.1 mIU/mL", normal: "1.5-9.3 mIU/mL", abnormal: false },
          { test: "FSH", value: "1.8 mIU/mL", normal: "1.4-18.1 mIU/mL", abnormal: false },
          { test: "Prolactin", value: "42 ng/mL", normal: "4-15 ng/mL", abnormal: true },
          { test: "MRI Pituitary", value: "Normal", normal: "Normal", abnormal: false },
        ],
        additionalInfo: "On chronic opioid therapy for back pain, BMI 32, fatigue",
        correctAnswer: "Opioid-Induced Hypogonadism",
        options: [
          "Opioid-Induced Hypogonadism",
          "Primary Hypogonadism",
          "Kallmann Syndrome",
          "Prolactinoma",
        ],
        explanation: "Low testosterone with low-normal gonadotropins (LH, FSH) indicates secondary hypogonadism. Chronic opioid use is a common cause, suppressing GnRH. Mildly elevated prolactin is common with opioids. Normal MRI rules out pituitary mass. Treatment: consider opioid reduction or testosterone replacement.",
      },
      {
        id: "endo-20",
        patientInfo: "55-year-old female with severe muscle weakness and fatigue",
        labResults: [
          { test: "Potassium", value: "2.4 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Cortisol (AM)", value: "38 μg/dL", normal: "7-25 μg/dL", abnormal: true },
          { test: "ACTH", value: "245 pg/mL", normal: "10-60 pg/mL", abnormal: true },
          { test: "DHEA-S", value: "Elevated", normal: "Normal", abnormal: true },
        ],
        additionalInfo: "Recent 15-lb weight loss, CT shows lung mass and bilateral adrenal hyperplasia",
        correctAnswer: "Ectopic ACTH Syndrome (Lung Cancer)",
        options: [
          "Ectopic ACTH Syndrome (Lung Cancer)",
          "Cushing's Disease",
          "Adrenal Adenoma",
          "Primary Hyperaldosteronism",
        ],
        explanation: "Very high ACTH (>100) with elevated cortisol, severe hypokalemia, and lung mass suggests ectopic ACTH syndrome from small cell lung cancer. Unlike pituitary Cushing's, doesn't suppress with high-dose dexamethasone. Hypokalemia from mineralocorticoid effect of high cortisol. Poor prognosis.",
      },
      {
        id: "endo-21",
        patientInfo: "30-year-old male with gynecomastia and infertility",
        labResults: [
          { test: "Total Testosterone", value: "520 ng/dL", normal: "300-1000 ng/dL", abnormal: false },
          { test: "Estradiol", value: "85 pg/mL", normal: "10-40 pg/mL", abnormal: true },
          { test: "LH", value: "8.5 mIU/mL", normal: "1.5-9.3 mIU/mL", abnormal: false },
          { test: "Prolactin", value: "12 ng/mL", normal: "4-15 ng/mL", abnormal: false },
          { test: "hCG", value: "Elevated", normal: "Undetectable in males", abnormal: true },
        ],
        additionalInfo: "Painless testicular mass discovered on exam, no drug use",
        correctAnswer: "Testicular Germ Cell Tumor",
        options: [
          "Testicular Germ Cell Tumor",
          "Prolactinoma",
          "Anabolic Steroid Abuse",
          "Liver Cirrhosis",
        ],
        explanation: "Elevated estradiol and hCG in a male with testicular mass indicates hCG-secreting germ cell tumor (choriocarcinoma or mixed). hCG has LH-like activity, stimulating testosterone and estrogen production causing gynecomastia. Normal testosterone/LH rules out hypogonadism. Urgent urology referral needed.",
      },
      {
        id: "endo-22",
        patientInfo: "8-year-old girl with early breast development and pubic hair",
        labResults: [
          { test: "LH (basal)", value: "5.2 mIU/mL", normal: "<0.3 mIU/mL prepubertal", abnormal: true },
          { test: "FSH", value: "4.8 mIU/mL", normal: "<2.5 mIU/mL prepubertal", abnormal: true },
          { test: "Estradiol", value: "45 pg/mL", normal: "<20 pg/mL prepubertal", abnormal: true },
          { test: "Bone Age", value: "11 years", normal: "8 years", abnormal: true },
        ],
        additionalInfo: "Height >97th percentile, breast Tanner stage 3, no CNS symptoms",
        correctAnswer: "Central Precocious Puberty",
        options: [
          "Central Precocious Puberty",
          "Peripheral Precocious Puberty",
          "Premature Thelarche",
          "Congenital Adrenal Hyperplasia",
        ],
        explanation: "Elevated gonadotropins (LH, FSH) with elevated estradiol indicates central (GnRH-dependent) precocious puberty. Advanced bone age confirms exposure to sex steroids. Idiopathic is most common in girls but MRI needed to rule out CNS lesion. Treatment: GnRH agonist to suppress puberty and preserve adult height.",
      },
      {
        id: "endo-23",
        patientInfo: "35-year-old female with 6-month history of fatigue, weight loss, and progressive hyperpigmentation of skin and buccal mucosa",
        labResults: [
          { test: "Cortisol (09:00 h)", value: "55 nmol/L", normal: ">450 nmol/L", abnormal: true },
          { test: "ACTH (plasma)", value: "580 pg/mL", normal: "10-50 pg/mL", abnormal: true },
          { test: "Short Synacthen Test", value: "Peak cortisol 210 nmol/L", normal: ">550 nmol/L", abnormal: true },
          { test: "Sodium", value: "128 mEq/L", normal: "135-145 mEq/L", abnormal: true },
          { test: "Potassium", value: "6.1 mEq/L", normal: "3.5-5.0 mEq/L", abnormal: true },
          { test: "Glucose", value: "52 mg/dL", normal: "70-100 mg/dL", abnormal: true },
        ],
        additionalInfo: "Hyperpigmentation of palmar creases, buccal mucosa, and old scars. Positive adrenal cortex antibodies (21-hydroxylase antibodies). Postural hypotension. No calcification on imaging.",
        correctAnswer: "Primary Adrenal Insufficiency (Addison's Disease)",
        options: [
          "Primary Adrenal Insufficiency (Addison's Disease)",
          "Secondary Adrenal Insufficiency (Pituitary)",
          "SIADH",
          "Primary Hyperaldosteronism",
        ],
        explanation: "Primary adrenal insufficiency: deficiency of both cortisol AND aldosterone. Cortisol deficiency → hyponatraemia, hypoglycaemia. Aldosterone deficiency → hyperkalaemia and salt wasting (OPPOSITE to Cushing's and Conn's). HIGH ACTH (feedback from low cortisol) causes hyperpigmentation (ACTH cross-reacts with MC1R). Secondary AI (pituitary) has LOW ACTH and no hyperpigmentation; aldosterone is preserved (ACTH-independent). Synacthen test confirms failure to respond. 80% autoimmune in UK. Treat with hydrocortisone + fludrocortisone. Sick-day rules essential — 'double, double, double' during illness.",
      },
      {
        id: "endo-24",
        patientInfo: "40-year-old male with paroxysmal headaches, palpitations, and profuse sweating with BP 210/130 during episodes",
        labResults: [
          { test: "Plasma Free Metanephrines", value: "4.8 nmol/L", normal: "<0.5 nmol/L", abnormal: true },
          { test: "Plasma Free Normetanephrines", value: "8.9 nmol/L", normal: "<0.9 nmol/L", abnormal: true },
          { test: "24h Urine Catecholamines", value: "6× upper limit", normal: "Normal", abnormal: true },
          { test: "Fasting Glucose", value: "11.2 mmol/L", normal: "<6.1 mmol/L", abnormal: true },
          { test: "Serum Calcium", value: "2.4 mmol/L", normal: "2.2-2.6 mmol/L", abnormal: false },
          { test: "Calcitonin", value: "Normal", normal: "Normal", abnormal: false },
        ],
        additionalInfo: "MRI adrenal: right adrenal mass 4.5 cm with central necrosis. Paroxysmal hypertension with headache, sweating, and palpitations (classic triad). SDHB gene mutation screening pending.",
        correctAnswer: "Phaeochromocytoma",
        options: [
          "Phaeochromocytoma",
          "Essential Hypertension",
          "Anxiety Disorder with Panic Attacks",
          "Primary Hyperaldosteronism (Conn's)",
        ],
        explanation: "Phaeochromocytoma: catecholamine-secreting adrenal medulla tumour. Classic triad: paroxysmal hypertension + headache + sweating. Plasma metanephrines are most sensitive test (>99% sensitivity). 10% rules: 10% malignant, 10% bilateral, 10% extra-adrenal (paraganglioma), 10% familial (MEN2, VHL, NF1, SDHB mutations — screen all patients). Hyperglycaemia from catecholamine effects (glycogenolysis, insulin suppression). CRITICAL: alpha-blockade (phenoxybenzamine) MUST precede beta-blockade to avoid hypertensive crisis from unopposed alpha-stimulation during surgery.",
      },
      {
        id: "endo-25",
        patientInfo: "45-year-old male with shoe size increasing from UK 9 to UK 12 over 5 years, jaw protrusion, and sleep apnoea",
        labResults: [
          { test: "IGF-1", value: "820 ng/mL", normal: "100-300 ng/mL (age-matched)", abnormal: true },
          { test: "GH after OGTT (75 g)", value: "8.2 ng/mL", normal: "<1.0 ng/mL (suppressed)", abnormal: true },
          { test: "Fasting Glucose (2h OGTT)", value: "9.8 mmol/L", normal: "<7.8 mmol/L", abnormal: true },
          { test: "Prolactin", value: "65 ng/mL", normal: "<15 ng/mL", abnormal: true },
          { test: "Random GH", value: "18 ng/mL", normal: "<5 ng/mL (varies)", abnormal: true },
        ],
        additionalInfo: "Coarse facial features, macroglossia, prognathism, carpal tunnel syndrome bilaterally. Visual fields: bitemporal hemianopia. MRI pituitary: 2.5 cm macroadenoma (>1 cm) with suprasellar extension.",
        correctAnswer: "Acromegaly",
        options: [
          "Acromegaly",
          "Gigantism",
          "Prolactinoma",
          "Cushing's Disease",
        ],
        explanation: "Acromegaly = GH excess after epiphyseal fusion (gigantism = before). IGF-1 elevated (best screening test) + failure of GH to suppress to <1 ng/mL with OGTT is diagnostic. GH stimulates IGF-1 from liver. Macroadenoma causes bitemporal hemianopia (optic chiasm compression). Mildly elevated prolactin = co-secretion or 'stalk effect'. Impaired glucose tolerance in ~25% (GH is anti-insulin). Complications: diabetes, hypertension, cardiomyopathy, colorectal polyps, OSA, arthritis. Treatment: transsphenoidal surgery (first-line); somatostatin analogues (octreotide/lanreotide) if residual or inoperable.",
      },
      {
        id: "endo-26",
        patientInfo: "26-year-old female with irregular periods (cycles 40-60 days), hirsutism, acne, and difficulty conceiving for 18 months",
        labResults: [
          { test: "LH", value: "18 mIU/mL", normal: "2-15 mIU/mL (follicular)", abnormal: true },
          { test: "FSH", value: "4.5 mIU/mL", normal: "2-10 mIU/mL", abnormal: false },
          { test: "LH:FSH ratio", value: "4:1", normal: "~1:1", abnormal: true },
          { test: "Total Testosterone", value: "3.8 nmol/L", normal: "0.5-2.5 nmol/L", abnormal: true },
          { test: "SHBG", value: "15 nmol/L", normal: "40-120 nmol/L", abnormal: true },
          { test: "Fasting Insulin", value: "28 μIU/mL", normal: "<15 μIU/mL", abnormal: true },
          { test: "AMH", value: "12.5 ng/mL", normal: "2.0-6.8 ng/mL (age 26)", abnormal: true },
        ],
        additionalInfo: "BMI 32. Pelvic ultrasound: ≥12 antral follicles per ovary (polycystic morphology), bilateral ovarian volume >10 mL. TSH and prolactin normal.",
        correctAnswer: "Polycystic Ovary Syndrome (PCOS)",
        options: [
          "Polycystic Ovary Syndrome (PCOS)",
          "Non-Classical Congenital Adrenal Hyperplasia",
          "Cushing's Syndrome",
          "Premature Ovarian Insufficiency",
        ],
        explanation: "PCOS diagnosis by Rotterdam criteria (2 of 3): oligo-anovulation, clinical or biochemical hyperandrogenism, polycystic ovarian morphology on USS. Elevated LH:FSH ratio (>2:1) is typical. Low SHBG (from insulin resistance) increases free androgen bioavailability. Elevated AMH reflects increased follicle pool. Insulin resistance (HOMA-IR) drives hyperandrogenism. Exclude hypothyroidism, hyperprolactinaemia, and non-classical CAH (17-OHP). Management: OCP for cycle regulation/hirsutism, metformin for insulin resistance/metabolic features, letrozole or clomiphene for ovulation induction.",
      },
      {
        id: "endo-27",
        patientInfo: "30-year-old female with 6 months of amenorrhoea, bilateral galactorrhoea, and infertility",
        labResults: [
          { test: "Prolactin", value: "4,200 mIU/L", normal: "<500 mIU/L (women)", abnormal: true },
          { test: "LH", value: "1.8 mIU/mL", normal: "2-15 mIU/mL", abnormal: true },
          { test: "FSH", value: "2.1 mIU/mL", normal: "2-10 mIU/mL", abnormal: false },
          { test: "Oestradiol", value: "85 pmol/L", normal: "200-800 pmol/L (follicular)", abnormal: true },
          { test: "TSH", value: "1.8 mIU/L", normal: "0.4-4.0 mIU/L", abnormal: false },
          { test: "Current Medications", value: "None", normal: "—", abnormal: false },
        ],
        additionalInfo: "MRI pituitary: 12 mm prolactin-secreting macroadenoma (>10 mm) with mild suprasellar extension. Headache and peripheral visual field blurring noted.",
        correctAnswer: "Prolactinoma",
        options: [
          "Prolactinoma",
          "Drug-Induced Hyperprolactinaemia",
          "Primary Hypothyroidism",
          "Physiological (Pregnancy-Related) Hyperprolactinaemia",
        ],
        explanation: "Prolactinoma is the most common functioning pituitary adenoma. Prolactin inhibits GnRH → low LH/FSH → hypo-oestrogenism → amenorrhoea, infertility, and osteoporosis risk. Galactorrhoea in ~50% of women. Very high prolactin (>3,000-5,000 mIU/L) suggests macro- or prolactin-secreting tumour. Drug causes (antipsychotics, metoclopramide, domperidone, SSRIs) typically cause levels <3,000 mIU/L. TRH elevation from hypothyroidism stimulates prolactin — exclude with TSH. Treatment: dopamine agonist (cabergoline first-line > bromocriptine) — shrinks tumour in most; surgery reserved for non-responders.",
      },
    ],
  }), []);

  useEffect(() => {
    const saved = loadSavedProgress();
    if (!saved) return;

    if (saved.progressByCategory) {
      setProgressByCategory(saved.progressByCategory);
    }

    const lastSession = saved.lastSession;
    if (!lastSession || !lastSession.selectedCategory) return;

    const cases = labCases[lastSession.selectedCategory];
    if (!cases) return;

    const caseMap = cases.reduce((acc, caseItem) => {
      acc[caseItem.id] = caseItem;
      return acc;
    }, {});
    const orderedCases = (lastSession.activeCaseIds || [])
      .map((caseId) => caseMap[caseId])
      .filter(Boolean);
    const restoredCases = orderedCases.length > 0 ? orderedCases : cases;
    const restoredIndex = Math.min(
      lastSession.caseIndex || 0,
      Math.max(restoredCases.length - 1, 0)
    );

    setSelectedCategory(lastSession.selectedCategory);
    setActiveCases(restoredCases);
    setCaseIndex(restoredIndex);
    setCurrentCase(restoredCases[restoredIndex] || restoredCases[0]);
    setScore(lastSession.score || 0);
    setCasesCompleted(lastSession.casesCompleted || 0);
    setSelectedAnswer(lastSession.selectedAnswer || null);
    setShowResult(Boolean(lastSession.showResult));
    setGamePhase(lastSession.gamePhase || "playing");
  }, [labCases]);

  useEffect(() => {
    if (!selectedCategory || activeCases.length === 0) return;

    const activeCaseIds = activeCases.map((caseItem) => caseItem.id);
    const existing = loadSavedProgress();
    const updatedProgressByCategory = {
      ...(existing?.progressByCategory || {}),
      [selectedCategory]: {
        casesCompleted,
        totalCases: activeCases.length,
      },
    };

    const nextProgress = {
      progressByCategory: updatedProgressByCategory,
      lastSession: {
        selectedCategory,
        gamePhase,
        caseIndex,
        score,
        casesCompleted,
        activeCaseIds,
        selectedAnswer,
        showResult,
      },
    };

    setProgressByCategory(updatedProgressByCategory);
    saveProgress(nextProgress);
  }, [
    selectedCategory,
    gamePhase,
    caseIndex,
    score,
    casesCompleted,
    activeCases,
    selectedAnswer,
    showResult,
  ]);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    const cases = labCases[categoryId];
    const shuffled = [...cases].sort(() => Math.random() - 0.5);
    setActiveCases(shuffled);
    setCurrentCase(shuffled[0]);
    setCaseIndex(0);
    setScore(0);
    setCasesCompleted(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setGamePhase("playing");
  };

  const handleAnswerSelect = (answer) => {
    if (!showResult) {
      setSelectedAnswer(answer);
    }
  };

  const handleSubmit = () => {
    if (selectedAnswer) {
      setShowResult(true);
      const isCorrect = selectedAnswer === currentCase.correctAnswer;

      if (isCorrect) {
        setScore(score + 1);
        // Award coins and XP
        const category = categories.find((c) => c.id === selectedCategory);
        const newUserData = {
          ...userData,
          coins: userData.coins + category.coins,
          xp: userData.xp + category.xp,
        };
        saveUserData(newUserData);
      }

      setCasesCompleted(casesCompleted + 1);
    }
  };

  const handleNext = () => {
    if (caseIndex + 1 < activeCases.length) {
      setCaseIndex(caseIndex + 1);
      setCurrentCase(activeCases[caseIndex + 1]);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      saveUserData({ ...userData, avatarStats: applyBoost(userData.avatarStats, GAME_BOOST) });
      setGamePhase("complete");
    }
  };

  const handleRestart = () => {
    if (!selectedCategory) return;
    const saved = loadSavedProgress();
    const updatedProgressByCategory = { ...(saved?.progressByCategory || {}) };
    delete updatedProgressByCategory[selectedCategory];

    const nextProgress = {
      progressByCategory: updatedProgressByCategory,
      lastSession: null,
    };

    setProgressByCategory(updatedProgressByCategory);
    saveProgress(nextProgress);
    handleCategorySelect(selectedCategory);
  };

  const handleBackToCategories = () => {
    setGamePhase("category");
    setSelectedCategory(null);
    setActiveCases([]);
    setCurrentCase(null);
    setCaseIndex(0);
    setScore(0);
    setCasesCompleted(0);
    setSelectedAnswer(null);
    setShowResult(false);
  };

  // Category Selection Screen
  if (gamePhase === "category") {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-lab.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-green-900/80 to-teal-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
        >
          ← Back to Games
        </button>

        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Lab Result Interpreter
          </h1>
          <p className="text-gray-600">
            Interpret lab values and make the correct diagnosis
          </p>
        </div>

        <h2 className="text-2xl font-bold text-white mb-6">Select Category</h2>

        <div className="grid md:grid-cols-2 gap-6">
          {categories.map((category) => {
            const CategoryIcon = category.icon;
            const totalCases = labCases[category.id].length;
            const completedCount = Math.min(
              progressByCategory[category.id]?.casesCompleted || 0,
              totalCases
            );
            const progressPercent =
              totalCases > 0 ? (completedCount / totalCases) * 100 : 0;
            return (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={
                  "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br " +
                  category.color +
                  " " +
                  category.borderColor
                }
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-white bg-opacity-80 rounded-full p-4 shadow-md">
                    <CategoryIcon className={"w-8 h-8 " + category.iconColor} />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-600 text-center mb-4">
                  {category.description}
                </p>
                <div className="flex items-center justify-center gap-4 text-sm">
                  <span className="font-semibold text-teal-600">
                    {category.coins} coins/case
                  </span>
                  <span className="font-semibold text-purple-600">
                    {category.xp} XP/case
                  </span>
                </div>
                <div className="mt-4">
                  <div className="h-2 bg-white/70 rounded-full">
                    <div
                      className="h-full bg-teal-600 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="mt-1 text-xs text-gray-600 text-center">
                    {completedCount} / {totalCases} completed
                  </div>
                </div>
              </button>
            );
          })}
        </div>
          </div>
        </div>
      </div>
    );
  }

  // Game Complete Screen
  if (gamePhase === "complete") {
    const percentage = activeCases.length > 0 ? Math.round((score / activeCases.length) * 100) : 0;
    const category = categories.find((c) => c.id === selectedCategory);

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-lab.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-green-900/80 to-teal-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
        >
          ← Back to Games
        </button>

        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
          <Award className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Game Complete!
          </h2>
          <p className="text-gray-600 mb-6">
            Category: {category.name}
          </p>

          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
            <div className="text-5xl font-bold text-teal-600 mb-2">
              {score}/{activeCases.length}
            </div>
            <div className="text-gray-700 text-lg mb-2">
              {percentage}% Correct
            </div>
            <div className="text-sm text-gray-600">
              You earned {score * category.coins} coins and {score * category.xp} XP!
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleRestart}
              className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              Play Again
            </button>
            <button
              onClick={handleBackToCategories}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Try Different Category
            </button>
            <button
              onClick={onBack}
              className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Back to Games
            </button>
          </div>
        </div>
          </div>
        </div>
      </div>
    );
  }

  // Playing Phase
  if (!currentCase) {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-lab.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-green-900/80 to-teal-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="text-center text-white">Loading cases...</div>
          </div>
        </div>
      </div>
    );
  }

  const isCorrect = showResult && selectedAnswer === currentCase.correctAnswer;
  const category = categories.find((c) => c.id === selectedCategory);

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "url('/avatar-lab.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-green-900/80 to-teal-900/80">
        <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={handleBackToCategories}
        className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
      >
        ← Change Category
      </button>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {category.name}
            </h1>
            <p className="text-sm text-gray-600">{category.description}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">
              Case {caseIndex + 1} of {activeCases.length}
            </div>
            <div className="text-lg font-bold text-teal-600">
              Score: {score}/{casesCompleted}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <button
            onClick={handleRestart}
            className="text-sm font-semibold text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Restart
          </button>
        </div>

        <div className="h-2 bg-gray-200 rounded-full mb-4">
          <div
            className="h-full bg-teal-600 rounded-full transition-all duration-300"
            style={{
              width: `${((caseIndex + 1) / activeCases.length) * 100}%`,
            }}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-2">
            Patient Information
          </h2>
          <p className="text-gray-700">{currentCase.patientInfo}</p>
        </div>

        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-3">
            Laboratory Results
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-3 border-b font-semibold text-gray-700">Test</th>
                  <th className="text-left p-3 border-b font-semibold text-gray-700">Result</th>
                  <th className="text-left p-3 border-b font-semibold text-gray-700">Normal Range</th>
                </tr>
              </thead>
              <tbody>
                {currentCase.labResults.map((lab, index) => (
                  <tr
                    key={index}
                    className={lab.abnormal ? "bg-red-50" : "bg-white"}
                  >
                    <td className="p-3 border-b text-gray-700">{lab.test}</td>
                    <td className={
                      "p-3 border-b font-semibold " +
                      (lab.abnormal ? "text-red-600" : "text-gray-700")
                    }>
                      {lab.value}
                      {lab.abnormal && " ⚠️"}
                    </td>
                    <td className="p-3 border-b text-gray-600 text-sm">{lab.normal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h2 className="text-sm font-bold text-gray-800 mb-2">
            Additional Clinical Information
          </h2>
          <p className="text-sm text-gray-700">{currentCase.additionalInfo}</p>
        </div>

        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-3">
            What is your interpretation?
          </h2>
          <div className="space-y-3">
            {currentCase.options.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrectOption = option === currentCase.correctAnswer;

              let buttonClass =
                "w-full text-left p-4 rounded-lg border-2 transition-all ";

              if (showResult) {
                if (isCorrectOption) {
                  buttonClass += "bg-green-50 border-green-500 ";
                } else if (isSelected && !isCorrectOption) {
                  buttonClass += "bg-red-50 border-red-500 ";
                } else {
                  buttonClass += "bg-gray-50 border-gray-200 opacity-50 ";
                }
              } else {
                if (isSelected) {
                  buttonClass += "bg-teal-50 border-teal-500 ";
                } else {
                  buttonClass +=
                    "bg-white border-gray-300 hover:border-teal-400 hover:bg-teal-50 ";
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={showResult}
                  className={buttonClass}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">
                      {option}
                    </span>
                    {showResult && isCorrectOption && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                    {showResult && isSelected && !isCorrectOption && (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {showResult && (
          <div
            className={
              "p-4 rounded-lg border-2 mb-6 " +
              (isCorrect
                ? "bg-green-50 border-green-500"
                : "bg-red-50 border-red-500")
            }
          >
            <div className="flex items-center gap-2 mb-2">
              {isCorrect ? (
                <Check className="w-6 h-6 text-green-600" />
              ) : (
                <X className="w-6 h-6 text-red-600" />
              )}
              <h3 className="font-bold text-gray-800">
                {isCorrect ? "Correct!" : "Incorrect"}
              </h3>
            </div>
            {isCorrect && (
              <p className="text-sm text-green-700 mb-2">
                +{category.coins} coins, +{category.xp} XP
              </p>
            )}
            <p className="text-sm text-gray-700 font-semibold mb-2">
              Correct Answer: {currentCase.correctAnswer}
            </p>
            <p className="text-sm text-gray-700">
              {currentCase.explanation}
            </p>
          </div>
        )}

        <div className="flex gap-3">
          {!showResult ? (
            <button
              onClick={handleSubmit}
              disabled={!selectedAnswer}
              className={
                "flex-1 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 " +
                (selectedAnswer
                  ? "bg-teal-600 text-white hover:bg-teal-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed")
              }
            >
              Submit Answer
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="flex-1 bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
            >
              Next Case
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default LabInterpreter;
